<template>
  <main class="short-video-app">
    <game-recharge-result v-if="gameRechargeResultVisible" />
    <game-recharge-page v-else-if="gameRechargePageVisible" />
    <template v-else>
    <h1 class="sr-only">{{ $t("seo.title") }}</h1>
    <payment-result v-if="paymentReturnVisible" @settled="handlePaymentSettled" @close="closePaymentResult" />
    <div class="ambient ambient-left" />
    <div class="ambient ambient-right" />

    <section :class="['video-shell', `root-${activeRootTab}`]">
      <header v-show="!profileVisible && !messageVisible && !discoverVisible" class="feed-header">
        <button class="brand-mini" data-analytics="root.home" :aria-label="$t('brand.home')" @click="showHome"><img src="/favicon.ico" alt=""></button>
        <nav class="channel-tabs" :aria-label="$t('a11y.videoChannels')">
          <button
            v-for="channel in channels"
            :key="channel.code"
            :class="{ active: activeChannel === channel.code }"
            :data-analytics="`home.channel.${channel.code}`"
            @click="switchChannel(channel)"
          >{{ channel.title || channel.name }}</button>
        </nav>
        <locale-switcher data-analytics="header.locale" />
        <button class="header-icon" data-analytics="header.search" :aria-label="$t('a11y.search')" @click="openSearch"><i class="el-icon-search" /></button>
      </header>

      <div v-show="!profileVisible && !messageVisible && !discoverVisible" ref="feed" class="video-feed" @scroll.passive="handleFeedScroll">
        <article
          v-for="(video, index) in videos"
          :key="video.id"
          :class="['video-slide', video.theme, { active: currentIndex === index, 'discovery-feed-slide': video.feed_kind === 'discovery' }]"
        >
          <template v-if="video.feed_kind === 'discovery'">
            <img class="discovery-feed-backdrop" :src="video.banner_url || video.cover_url" :alt="video.title" @error="hideDiscoveryFeedImage">
            <div class="discovery-feed-shade" />
            <div class="discovery-feed-card">
              <div class="discovery-feed-type">
                <span><i :class="discoveryFeedIcon(video.content_type)" aria-hidden="true" /></span>
                <div><small>{{ $t("feedDiscovery.eyebrow") }}</small><strong>{{ $t(`feedDiscovery.types.${video.content_type}`) }}</strong></div>
                <em v-if="video.badge">{{ video.badge }}</em>
              </div>
              <img class="discovery-feed-cover" :src="video.cover_url" :alt="video.title" @error="hideDiscoveryFeedImage">
              <div class="discovery-feed-copy">
                <small>{{ discoveryFeedMeta(video) }}</small>
                <h2>{{ video.title }}</h2>
                <p>{{ video.description }}</p>
                <span><i class="el-icon-data-analysis" aria-hidden="true" /> {{ $t("feedDiscovery.heat", { count: formatCount(video.heat) }) }}</span>
              </div>
              <button type="button" :data-analytics="`discover.content.${video.content_type}`" @click="openFeedDiscovery(video)">
                {{ $t(`feedDiscovery.actions.${video.content_type}`) }} <i class="el-icon-right" aria-hidden="true" />
              </button>
              <small class="discovery-feed-frequency">{{ $t("feedDiscovery.recommendation") }}</small>
            </div>
          </template>
          <template v-else>
          <div class="scene-art" aria-hidden="true">
            <span class="scene-orb" />
            <span class="scene-line line-one" />
            <span class="scene-line line-two" />
            <span class="scene-word">{{ video.title.slice(0, 1) }}</span>
            <span class="scene-grain" />
          </div>

          <img
            v-if="!isMediaWindow(index) && video.cover_url"
            class="video-cover-placeholder"
            :src="video.cover_url"
            :alt="video.title"
            loading="lazy"
          >
          <video
            v-if="isMediaWindow(index) && video.available && video.content_type === 'video'"
            ref="videoPlayers"
            class="video-player"
            :data-video-id="video.id"
            :src="video.live_source === 'tiktok' ? null : video.play_url"
            :poster="video.cover_url"
            :muted="muted"
            playsinline
            webkit-playsinline
            loop
            :preload="videoPreloadMode(video, index)"
            data-analytics="video.play_toggle"
            @click.stop.prevent="handleVideoSurfaceClick(video, $event)"
            @dblclick.stop.prevent
            @loadeddata="handleLoaded(video)"
            @waiting="handleVideoWaiting(video)"
            @canplay="handleCanPlay(video)"
            @play="handlePlay(video)"
            @pause="handlePause(video)"
            @timeupdate="handleTimeUpdate(video, $event)"
            @error="handleVideoError(video)"
          />
          <image-post-gallery v-if="isMediaWindow(index) && video.available && video.content_type === 'image_text'" :images="video.image_urls" :title="video.title" :content-id="video.id" />
          <audio
            v-if="isMediaWindow(index) && video.available && video.content_type === 'image_text' && video.music_audio_url"
            ref="imagePostAudioPlayers"
            :data-video-id="video.id"
            :src="video.music_audio_url"
            :muted="muted"
            loop
            :preload="audioPreloadMode(index)"
            @canplay="handleImageMusicReady(video)"
            @error="handleImageMusicError(video)"
          />
          <button
            v-if="isMediaWindow(index) && video.available && video.content_type === 'live'"
            type="button"
            class="live-preview"
            data-analytics="live.enter"
            :aria-label="$t('liveRoom.enter')"
            @click="openLiveRoom(video)"
          >
            <img :src="video.cover_url" :alt="video.title">
            <span><i class="el-icon-video-play" /> {{ $t("liveRoom.enter") }}</span>
          </button>
          <div class="video-shade" />

          <div v-if="!video.available || video.load_state === 'error'" class="video-placeholder">
            <i :class="video.available ? 'el-icon-warning-outline' : 'el-icon-lock'" />
            <strong>{{ video.available ? $t("feed.videoFailed") : $t("feed.unavailable") }}</strong>
            <p>{{ video.available ? $t("feed.checkNetwork") : $t("feed.unavailable") }}</p>
            <button v-if="video.available" data-analytics="video.retry" @click="retryVideo(video)">{{ $t("common.retry") }}</button>
            <button v-else @click="showAuth('login', { action: 'reload' })">{{ $t("feed.login") }}</button>
          </div>

          <transition name="play-pop">
            <button v-if="video.available && video.content_type === 'video' && video.load_state === 'ready' && video.user_paused && currentIndex === index" class="center-play" data-analytics="video.play_toggle" @click.stop.prevent="handleVideoSurfaceClick(video, $event)" @dblclick.stop.prevent>
              <i class="el-icon-video-play" />
            </button>
          </transition>
          <transition name="heart-burst">
            <span v-if="video.like_burst" :key="`like-burst-${video.id}-${video.like_burst_key}`" class="video-like-burst" aria-hidden="true"><star-live-heart-icon selected /></span>
          </transition>

          <div v-if="video.content_type === 'live'" class="video-status">
            <span class="live-label"><i /> LIVE</span>
          </div>
          <span v-if="weakNetwork" class="quality-label">{{ $t("feed.qualityLow") }}</span>

          <button v-if="video.content_type === 'video' || (video.content_type === 'image_text' && video.music_audio_url)" class="sound-control" data-analytics="video.mute_toggle" :aria-label="muted ? $t('feed.muteOn') : $t('feed.muteOff')" @click="toggleMute">
            <i :class="muted ? 'el-icon-close-notification' : 'el-icon-headset'" />
          </button>

          <aside class="action-rail">
            <button
              type="button"
              class="creator-avatar"
              data-analytics="video.author_open"
              :class="{ 'is-live': video.content_type === 'live' }"
              :aria-label="video.content_type === 'live' ? `${video.author.name} · ${$t('appUi.liveNow')}` : video.author.name"
              @click.stop="openAuthor(video.author.id)"
            >
              <platform-avatar :src="video.author.avatar_url" :alt="video.author.name" />
              <i v-if="!video.author.followed && !video.author.is_self" data-analytics="video.author_follow" @click.stop.prevent="toggleFollow(video)">+</i>
            </button>
            <button v-if="video.content_type !== 'live'" data-analytics="video.like" :disabled="video.action_loading && video.action_loading.like" :class="{ selected: video.user_state.liked }" @click="toggleAction(video, 'like')">
              <span class="action-icon heart-action-icon"><star-live-heart-icon :selected="video.user_state.liked" /></span>
              <small>{{ formatCount(video.stats.like) }}</small>
            </button>
            <button v-if="video.content_type !== 'live'" data-analytics="video.comments_open" @click="openComments(video)">
              <span class="action-icon"><i class="el-icon-chat-dot-round" /></span>
              <small>{{ formatCount(video.stats.comment) }}</small>
            </button>
            <button v-if="video.content_type !== 'live'" data-analytics="video.favorite" :class="{ selected: video.user_state.favorited }" @click="toggleAction(video, 'favorite')">
              <span class="action-icon"><i class="el-icon-collection-tag" /></span>
              <small>{{ formatCount(video.stats.favorite) }}</small>
            </button>
            <button data-analytics="video.share" @click="openShare(video)">
              <span class="action-icon"><i class="el-icon-share" /></span>
              <small>{{ formatCount(video.stats.share) }}</small>
            </button>
            <button data-analytics="video.more" @click="openMore(video)">
              <span class="action-icon more-icon"><i class="el-icon-more" /></span>
              <small>{{ $t("feed.more") }}</small>
            </button>
          </aside>

          <div class="video-copy">
            <div class="author-line" data-analytics="video.author_open" role="button" @click="openAuthor(video.author.id)">
              <strong>@{{ video.author.name }} <i v-if="video.author.verified" class="el-icon-success" /></strong>
              <button v-if="!video.author.is_self && !video.author.followed" data-analytics="video.author_follow" @click.stop="toggleFollow(video)">{{ $t("feed.follow") }}</button>
              <button v-else-if="!video.author.is_self" class="followed" data-analytics="video.author_follow" @click.stop="toggleFollow(video)">{{ $t("feed.followed") }}</button>
            </div>
            <h2>{{ video.title }}</h2>
            <p>{{ video.description }}</p>
            <div class="topic-line"><span v-for="(tag, tagIndex) in video.tags" :key="`${video.id}-${tagIndex}`">#{{ tag }}</span></div>
            <div v-if="video.music_name" class="music-line"><i class="el-icon-headset" aria-hidden="true" /><span>{{ video.music_name }}</span></div>
          </div>

          <div v-if="video.content_type === 'video'" class="slide-progress" :class="{ buffering: video.load_state === 'loading' }"><span :style="{ width: `${video.progress}%` }" /></div>
          </template>
        </article>

        <div v-if="!feedLoading && !feedStartupRetrying && videos.length === 0" class="feed-empty">
          <i class="el-icon-video-camera" />
          <strong>{{ $t(isLiveChannel ? "liveRoom.noLive" : "feed.preparing") }}</strong>
          <p v-if="isLiveChannel">{{ $t("liveRoom.noLiveTip") }}</p>
          <p v-else>{{ $t("feed.preparingTip") }}</p>
          <button v-if="isLiveChannel" @click="loadFeed">{{ $t("feed.refresh") }}</button>
        </div>
      </div>

      <div v-if="(feedLoading || feedStartupRetrying) && !profileVisible && !messageVisible && !discoverVisible" class="feed-loading"><i class="el-icon-loading" /> {{ $t("feed.loading") }}</div>

      <transition :name="liveRoomTransitionName">
        <live-room
          v-if="liveRoomVideo"
          :key="liveRoomVideo.id"
          ref="liveRoom"
          :video="liveRoomVideo"
          :current-user="currentUser"
          @close="closeLiveRoom"
          @ended="handleLiveEnded"
          @switch-live="switchLiveRoom"
          @login="showAuth('login', { action: 'live_gift', videoId: liveRoomVideo.id })"
          @author="openLiveRoomAuthor"
          @share="openShare"
          @message-user="openLiveRoomMessage"
          @profile-user="openLiveRoomProfile"
          @gift-sent="refreshNotificationInbox"
          @recharged="refreshNotificationInbox"
        />
      </transition>

      <nav v-show="!settingsVisible && !legalVisible" class="bottom-nav" :aria-label="$t('nav.home')" @dblclick.stop.prevent>
        <button data-analytics="root.home" :class="{ active: activeRootTab === 'home' }" @click="showHome"><i class="el-icon-house" /><span>{{ $t("nav.home") }}</span></button>
        <button data-analytics="root.friends" class="message-tab-button" :class="{ active: activeRootTab === 'message' }" @click="handleMessages">
          <i class="friend-handshake-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <path d="M3 4h6l2.2 2.2" />
              <path d="M21 4h-5.2l-4.1 4.1a1.45 1.45 0 0 1-2.05-2.05l2.45-2.45" />
              <path d="m3 5-1 10h3l5.7 5.1a1.35 1.35 0 0 0 1.9-1.9" />
              <path d="m11.2 16.8 1.8 1.7a1.35 1.35 0 0 0 1.9-1.9" />
              <path d="m13.4 14.7 2 1.9a1.35 1.35 0 0 0 1.9-1.9l-4.1-4" />
              <path d="m21 5 1 10h-3l-1.5-1.5" />
            </svg>
          </i>
          <span>{{ $t("nav.friends") }}</span><em v-if="notificationUnread">{{ notificationUnread > 99 ? "99+" : notificationUnread }}</em>
        </button>
        <button type="button" class="studio-tab-button" data-analytics="root.publish" :class="{ active: publishVisible }" :aria-label="$t('publishUi.title')" @click="openUserPublish">
          <i class="studio-plus-icon" aria-hidden="true"><b /><b /></i>
        </button>
        <button data-analytics="root.discover" :class="{ active: activeRootTab === 'discover' }" @click="handleDiscover"><i class="el-icon-discover" /><span>{{ $t("nav.discover") }}</span></button>
        <button data-analytics="root.profile" :class="{ active: activeRootTab === 'profile' }" @click="handleProfile"><i class="el-icon-user-solid" /><span>{{ $t("nav.profile") }}</span></button>
      </nav>

      <search-discovery
        v-if="searchVisible"
        ref="searchPage"
        :logged-in="!!currentUser"
        @close="closeSearch"
        @open-user="openSearchUser"
        @login="requestSearchLogin"
        @select-video="selectSearchVideo"
      />
      <discover-page
        v-if="discoverVisited"
        v-show="discoverVisible"
        class="app-root-page"
        ref="discoverPage"
        :logged-in="!!currentUser"
        :user-avatar="currentUser ? avatarUrl(currentUser.avatar_url) : ''"
        @music-state-change="handleMusicStateChange"
        @login="requestSearchLogin"
        @share-content="openChatShare"
        @open-live="openLiveRoom"
      />
      <user-center
        v-if="profileVisited"
        v-show="profileVisible"
        class="app-root-page"
        ref="userCenter"
        @logout="logoutCurrentUser"
        @select-video="selectCenterVideo"
        @open-discover-content="openCenterDiscoverContent"
        @open-paid="openCenterPaidContent"
        @open-author="openCenterAuthor"
        @open-chat="openDirectChat"
        @back-to-chat="handleMessages"
        @profile-updated="syncCenterProfile"
        @notification-created="refreshNotificationInbox"
        @open-studio="openStudio"
        @open-live="openStudioLive"
        @open-fan-group="openFanGroupFromStudio"
        @open-settings="openSettings"
        @open-decoration-store="openDecorationStore"
      />
      <notification-center
        v-if="messageVisited"
        v-show="messageVisible"
        class="app-root-page"
        ref="notificationCenter"
        :current-user-id="Number(currentUser && currentUser.id || 0)"
        @unread-change="notificationUnread = $event"
        @select-video="selectNotificationVideo"
        @select-comment="selectNotificationComment"
        @open-transactions="openNotificationTransactions"
        @open-paid="openNotificationPaidContent"
        @open-profile="openNotificationProfile"
        @open-studio="openStudio"
        @open-guild="openNotificationGuild"
        @open-support="openNotificationSupport"
        @open-chat-peer="openChatPeerProfile"
        @open-chat-content="openChatSharedContent"
        @play-chat-music="startChatMusic"
      />
      <account-settings
        v-if="settingsVisible"
        :user="currentUser || {}"
        :initial-panel="settingsInitialPanel"
        :guild-request-id="settingsGuildRequestId"
        :support-ticket-id="settingsSupportTicketId"
        @close="closeSettings"
        @open-legal="openLegalDocument"
        @open-profile="openProfileSettings"
        @open-notifications="openNotificationSettings"
        @open-pwa-install="openPWAInstall"
        @email-updated="syncSecurityEmail"
        @guild-closed="clearGuildSettingsTarget"
        @support-closed="clearSupportSettingsTarget"
        @guild-membership-changed="handleGuildMembershipChanged"
        @data-erased="handlePrivacyDataErased"
        @account-deletion-requested="handleAccountDeletionRequested"
        @logout="logoutCurrentUser"
      />
      <user-publish-composer ref="userPublish" :visible.sync="publishVisible" @published="handleUserPublished" />

      <transition name="mini-music">
        <aside
          v-if="miniMusic && miniMusic.track && !(discoverVisible && miniMusic.full_visible)"
          class="mini-music-player"
          :aria-label="$t('musicUi.miniPlayer')"
        >
          <button class="mini-music-main" :aria-label="$t('musicUi.openLyrics')" @click="openMiniMusicLyrics">
            <span class="mini-music-cover" :class="{ playing: miniMusic.playing }"><img :src="miniMusic.track.cover_url" :alt="miniMusic.track.title" @error="hideMiniMusicCover"><i class="el-icon-headset" /></span>
            <span class="mini-music-copy"><strong>{{ miniMusic.track.title }}</strong><small>{{ miniMusic.track.artist }}</small><i><b :style="{ width: `${miniMusicProgress}%` }" /></i></span>
          </button>
          <button class="mini-music-toggle" :aria-label="$t(miniMusic.playing ? 'musicUi.pause' : 'musicUi.play')" @click="toggleMiniMusic">
            <i :class="miniMusic.playing ? 'el-icon-video-pause' : 'el-icon-video-play'" />
          </button>
          <button class="mini-music-close" :aria-label="$t('musicUi.closePlayer')" @click="closeMiniMusic">
            <i class="el-icon-close" />
          </button>
        </aside>
      </transition>
    </section>

    <!-- 放在视频外壳的堆叠上下文之外，确保注册弹窗打开时法律文档仍位于最上层。 -->
    <legal-document-viewer
      v-if="legalVisible"
      :type="legalType"
      @close="closeLegalDocument"
    />

    <image-viewer />

    <div class="desktop-note">
      <span>{{ messageVisible ? 'STARLIVE INBOX' : profileVisible ? 'STARLIVE MEMBER' : discoverVisible ? 'STARLIVE DISCOVER' : 'STARLIVE' }}</span>
      <strong v-if="messageVisible">{{ $t("desktop.inbox") }}</strong><strong v-else-if="profileVisible">{{ $t("desktop.profile") }}</strong><strong v-else-if="discoverVisible">{{ $t("desktop.discover") }}</strong><strong v-else>{{ $t("desktop.feed") }}</strong>
      <p>{{ $t(messageVisible ? 'desktop.inboxDescription' : profileVisible ? 'desktop.profileDescription' : discoverVisible ? 'desktop.discoverDescription' : 'desktop.feedDescription') }}</p>
    </div>

    <el-dialog custom-class="auth-dialog" :visible.sync="authVisible" width="92%" :close-on-click-modal="false" append-to-body @closed="resetAuthForm">
      <div class="auth-head"><span>STARLIVE MEMBER</span><h2>{{ authTitle }}</h2><p>{{ authDescription }}</p></div>
      <div v-if="!['reset','restore'].includes(authMode)" class="auth-tabs"><button :class="{ active: authMode === 'login' }" @click="switchAuthMode('login')">{{ $t("appUi.login") }}</button><button :class="{ active: authMode === 'register' }" @click="switchAuthMode('register')">{{ $t("appUi.register") }}</button></div>
      <button v-else class="auth-back" @click="switchAuthMode('login')"><i class="el-icon-back" /> {{ $t("appUi.backLogin") }}</button>
      <el-form ref="authForm" :model="authForm" :rules="authRules" label-position="top">
        <el-form-item :label="authAccountLabel" prop="email"><el-input v-model.trim="authForm.email" maxlength="100" :placeholder="authAccountPlaceholder" prefix-icon="el-icon-user" /></el-form-item>
        <div v-if="authMode === 'login'" class="login-methods"><button type="button" :class="{ active: loginMethod === 'password' }" @click="switchLoginMethod('password')">{{ $t("appUi.passwordLogin") }}</button><button type="button" :class="{ active: loginMethod === 'code' }" @click="switchLoginMethod('code')">{{ $t("appUi.codeLogin") }}</button></div>
        <el-form-item v-if="needsCode" :label="$t('appUi.code')" prop="code"><div class="code-row"><el-input v-model.trim="authForm.code" maxlength="6" :placeholder="$t('appUi.codePlaceholder')" inputmode="numeric" /><el-button :disabled="countdown > 0" :loading="codeSending" @click="sendCode">{{ countdown > 0 ? `${countdown}s` : $t("appUi.getCode") }}</el-button></div></el-form-item>
        <el-form-item v-if="needsPassword" :label="$t(authMode === 'reset' ? 'appUi.newPassword' : 'appUi.password')" prop="password"><el-input v-model="authForm.password" type="password" show-password maxlength="72" autocomplete="current-password" :placeholder="$t('appUi.passwordPlaceholder')" prefix-icon="el-icon-lock" /></el-form-item>
        <el-form-item v-if="needsPasswordConfirmation" :label="$t('appUi.confirmPassword')" prop="passwordConfirmation"><el-input v-model="authForm.passwordConfirmation" type="password" show-password maxlength="72" autocomplete="new-password" :placeholder="$t('appUi.confirmPasswordPlaceholder')" prefix-icon="el-icon-lock" /></el-form-item>
        <button v-if="authMode === 'login' && loginMethod === 'password'" type="button" class="forgot-password" @click="switchAuthMode('reset')">{{ $t("appUi.forgotPassword") }}</button>
        <button v-if="authMode === 'login'" type="button" class="forgot-password privacy-recovery-link" @click="switchAuthMode('restore')">{{ $t("privacyManagement.recoveryLink") }}</button>
        <div v-if="authMode === 'register'" class="agreement-block">
          <div class="agreement-row">
            <el-checkbox v-model="authForm.agreement" class="agreement-check" :aria-label="$t('appUi.agreeTerms')" />
            <span>{{ $t("legalUi.registerPrefix") }} <button type="button" @click="openLegalDocument('terms')">{{ $t("settingsUi.terms") }}</button></span>
          </div>
          <div class="agreement-row">
            <el-checkbox v-model="authForm.privacy" class="agreement-check" :aria-label="$t('appUi.agreePrivacy')" />
            <span>{{ $t("legalUi.registerPrefix") }} <button type="button" @click="openLegalDocument('privacy')">{{ $t("settingsUi.privacy") }}</button></span>
          </div>
        </div>
        <el-checkbox v-model="authForm.remember" class="remember-check">{{ $t("appUi.remember") }}</el-checkbox>
        <el-button class="auth-submit" :loading="authLoading" @click="submitAuth">{{ authSubmitText }}</el-button>
      </el-form>
    </el-dialog>

    <el-drawer :title="$t('dialog.comments')" :visible.sync="commentsVisible" direction="btt" size="68%" custom-class="comments-drawer" append-to-body>
      <div ref="commentList" class="comment-list">
        <article v-for="comment in threadedComments" :key="comment.id" :data-comment-id="comment.id" :class="{ reply: comment.parent_id > 0, focused: focusedCommentId === comment.id }">
          <avatar-with-frame class="comment-avatar-shell" :avatar="comment.avatar_url" :avatar-alt="comment.user_name" :frame-animation-url="comment.avatar_frame_url" :frame-preview-url="comment.avatar_frame_preview_url" />
          <div class="comment-content">
            <strong>{{ comment.user_name }} <em v-if="comment.reply_to_name">{{ $t("common.reply") }} {{ comment.reply_to_name }}</em></strong>
            <p>{{ comment.content }}</p>
            <div class="comment-meta">
              <small>{{ $d(comment.create_time, { dateStyle: "medium", timeStyle: "short" }) }}</small>
              <button v-if="commentsEnabled" @click="startReply(comment)">{{ $t("appUi.reply") }}</button>
              <button v-if="comment.can_delete || comment.is_owner" @click="removeComment(comment)">{{ $t("appUi.delete") }}</button>
              <button v-else @click="openReport('comment', comment.id)">{{ $t("appUi.report") }}</button>
              <button v-if="!comment.is_owner" @click="blockCommentUser(comment)">{{ $t("appUi.block") }}</button>
            </div>
          </div>
          <button :class="['comment-like', { selected: comment.liked }]" @click="toggleCommentLike(comment)"><i class="el-icon-star-off" /><small>{{ comment.like_count || '' }}</small></button>
        </article>
        <p v-if="comments.length === 0" class="comment-empty">{{ $t("appUi.noComments") }}</p>
      </div>
      <div v-if="replyingComment && commentsEnabled" class="reply-tip"><span>{{ $t("appUi.replyTo", { name: replyingComment.user_name }) }}</span><button @click="cancelReply">{{ $t("common.cancel") }}</button></div>
      <div v-if="commentsEnabled" class="comment-editor"><el-input v-model.trim="commentText" maxlength="300" :placeholder="replyingComment ? $t('appUi.replyPlaceholder', { name: replyingComment.user_name }) : $t('appUi.commentPlaceholder')" @keyup.enter.native="postComment" /><button @click="postComment">{{ $t("appUi.send") }}</button></div>
      <div v-else class="comment-editor comment-editor-disabled"><i class="el-icon-turn-off" />{{ $t("studioComments.disabledHint") }}</div>
    </el-drawer>

    <el-drawer :title="$t('dialog.authorProfile')" :visible.sync="authorVisible" direction="rtl" size="100%" custom-class="author-drawer" append-to-body>
      <div v-if="authorDetail" class="author-profile">
        <div class="author-hero">
          <button
            type="button"
            :class="['author-avatar', { 'is-live': authorDetail.live_room }]"
            :disabled="!authorDetail.live_room"
            :aria-label="authorDetail.live_room ? `${authorDetail.author.name} · ${$t('appUi.liveNow')}` : $t('centerDetail.avatarAlt')"
            @click="openAuthorLive"
          >
            <platform-avatar :src="authorDetail.author.avatar_url" :alt="authorDetail.author.name" />
            <em v-if="authorDetail.live_room"><i />{{ $t("appUi.liveNow") }}</em>
          </button>
          <div><h2>{{ authorDetail.author.name }} <i v-if="authorDetail.author.verified" class="el-icon-success" /></h2><p>@{{ authorDetail.author.account }}</p></div>
        </div>
        <p class="author-bio">{{ authorDetail.author.bio }}</p>
        <div class="author-stats"><div><strong>{{ formatCount(authorDetail.author.follower_count) }}</strong><span>{{ $t("centerUi.fans") }}</span></div><div><strong>{{ formatCount(authorDetail.author.like_count) }}</strong><span>{{ $t("centerUi.receivedLikes") }}</span></div></div>
        <div v-if="!authorDetail.author.is_self && !authorDetail.author.blocked" class="author-primary-actions">
          <button class="author-follow" :class="{ followed: authorDetail.author.followed }" @click="toggleAuthorDetailFollow">{{ $t(authorDetail.author.followed ? "feed.followed" : "feed.follow") }}</button>
          <button class="author-message" type="button" @click="openDirectChat({ target_type: 'author', target_id: authorDetail.author.id })"><i class="el-icon-chat-dot-round" />{{ $t("directMessage.messageUser") }}</button>
        </div>
        <section v-if="!authorDetail.author.blocked && authorDetail.fan_group" class="author-fan-group">
          <platform-avatar :src="authorDetail.fan_group.avatar_url || authorDetail.author.avatar_url" :alt="authorDetail.fan_group.name" />
          <span><small>{{ $t('fanGroup.officialGroup') }}</small><strong>{{ authorDetail.fan_group.name }}</strong><em>{{ $t('fanGroup.memberCount', { count: $n(authorDetail.fan_group.member_count) }) }}</em></span>
          <button type="button" :disabled="fanGroupJoining" :data-analytics="authorDetail.fan_group.joined ? 'author.fan_group_open' : 'author.fan_group_join'" @click="joinOrOpenAuthorFanGroup"><i v-if="fanGroupJoining" class="el-icon-loading" />{{ $t(authorDetail.fan_group.joined ? 'fanGroup.enter' : 'fanGroup.join') }}</button>
        </section>
        <button v-else-if="authorDetail.author.blocked" class="author-follow followed" @click="toggleAuthorBlock">{{ $t("appUi.blockedTapUndo") }}</button>
        <div v-if="!authorDetail.author.is_self" class="author-secondary-actions"><button @click="toggleAuthorBlock">{{ $t(authorDetail.author.blocked ? "appUi.unblock" : "appUi.blockAuthor") }}</button><button @click="openReport('author', authorDetail.author.id)">{{ $t("appUi.reportAccount") }}</button></div>
        <nav v-if="!authorDetail.author.blocked" class="author-work-tabs" :aria-label="$t('a11y.authorContent')">
          <button :class="{ active: authorWorkTab === 'video' }" @click="switchAuthorWorkTab('video')">{{ $t("appUi.works") }} <small>{{ authorDetail.work_counts.video }}</small></button>
          <button :class="{ active: authorWorkTab === 'support' }" @click="switchAuthorWorkTab('support')">{{ $t("creator.supportTab") }} <small>{{ authorShop ? authorShop.gifts.length + authorShop.tip_projects.length : 0 }}</small></button>
          <button :class="{ active: authorWorkTab === 'paid' }" @click="switchAuthorWorkTab('paid')">{{ $t("appUi.exclusive") }} <small>{{ authorShop ? authorShop.paid_contents.length : 0 }}</small></button>
        </nav>
        <section v-if="!authorDetail.author.blocked && authorWorkTab === 'video'" class="author-works-panel">
          <div v-if="authorWorksLoading && !authorDetail.videos.length" class="author-work-skeleton"><i v-for="index in 6" :key="index" /></div>
          <div v-else-if="authorDetail.videos.length" class="author-grid">
            <button v-for="item in authorDetail.videos" :key="item.id" class="author-work-card" @click="openAuthorWork(item)">
              <img v-if="item.cover_url" :src="item.cover_url" :alt="item.title" loading="lazy" />
              <span v-else :class="['author-work-fallback', item.theme]">{{ item.title.slice(0, 1) }}</span>
              <i class="author-work-shade" />
              <em v-if="!item.available" class="author-work-lock"><i class="el-icon-lock" /> {{ $t("appUi.loginToContinue") }}</em>
              <em v-else-if="item.content_type === 'live'" class="author-work-live"><i /> {{ $t(item.live_status ? "appUi.liveNow" : "appUi.replay") }}</em>
              <strong>{{ item.title }}</strong>
              <small><i class="el-icon-video-play" /> {{ formatCount(item.view_count) }} <b>♥ {{ formatCount(item.like_count) }}</b></small>
              <time v-if="item.content_type === 'image_text'"><i class="el-icon-picture-outline" /></time>
            </button>
          </div>
          <div v-else class="author-work-empty"><i class="el-icon-video-camera" /><strong>{{ $t("creator.noWorks") }}</strong><p>{{ $t("appUi.worksEmptyTip") }}</p></div>
          <button v-if="authorDetail.pagination.has_more" class="author-load-more" :disabled="authorWorksLoading" @click="loadMoreAuthorWorks">{{ $t(authorWorksLoading ? "appUi.loadingMore" : "appUi.loadMoreWorks") }}</button>
        </section>
        <section v-else-if="!authorDetail.author.blocked && authorWorkTab === 'support'" class="creator-commerce author-support-panel">
          <div class="commerce-head"><div><small>CREATOR SUPPORT</small><h3>{{ $t("creator.shop") }}</h3></div><button @click="openWalletDetails"><i class="el-icon-coin" /> {{ authorShop && authorShop.wallet ? authorShop.wallet.balance : '--' }}</button></div>
          <template v-if="authorShop">
            <h4>{{ $t("giftUi.sectionTitle") }}</h4>
            <div class="gift-grid">
              <button v-for="gift in authorShop.gifts" :key="gift.id" @click="openGift(gift)">
                <span>{{ gift.icon }}</span><strong>{{ gift.name }}</strong><em>{{ $n(gift.coin_price) }} {{ $t("wallet.coins") }}</em>
              </button>
            </div>
            <h4>{{ $t("creator.tips") }}</h4>
            <div class="tip-projects"><button v-for="project in authorShop.tip_projects" :key="project.id" @click="openTip(project)"><i class="el-icon-coffee-cup" /><span><strong>{{ project.title }}</strong><small>{{ project.description }}</small></span><em>{{ $n(project.coin_price) }} {{ $t("wallet.coins") }}</em></button></div>
          </template>
          <div v-else-if="authorShopLoading" class="author-work-skeleton" aria-hidden="true"><i v-for="index in 6" :key="index" /></div>
        </section>
        <section v-else-if="!authorDetail.author.blocked && authorWorkTab === 'paid'" class="author-paid-panel">
          <div v-if="authorShop && authorShop.paid_contents.length" class="paid-content-grid"><button v-for="content in authorShop.paid_contents" :key="content.id" :class="content.theme" @click="openPaidContent(content)"><img v-if="content.cover_url" :src="content.cover_url" :alt="content.title" loading="lazy" /><span v-else class="paid-cover-letter">{{ content.title.slice(0, 1) }}</span><i :class="content.purchased ? 'el-icon-unlock' : 'el-icon-lock'" /><strong>{{ content.title }}</strong><small>{{ $t(content.content_type === 'album' ? "appUi.paidAlbum" : "appUi.paidVideo") }} · {{ content.visibility === "purchased_only" ? $t("appUi.purchasedVisible") : content.visibility }}</small><em>{{ content.purchased ? $t("appUi.purchased") : $t("appUi.coinPrice", { amount: $n(content.coin_price) }) }}</em></button></div>
          <div v-else-if="authorShopLoading" class="author-work-skeleton" aria-hidden="true"><i v-for="index in 6" :key="index" /></div>
          <div v-else class="author-work-empty"><i class="el-icon-lock" /><strong>{{ $t("creator.noPremium") }}</strong><p>{{ $t("appUi.noPremiumTip") }}</p></div>
          <p v-if="authorShop" class="offline-policy">{{ authorShop.offline_policy }}</p>
        </section>
      </div>
      <div v-else-if="authorLoading" class="author-profile author-profile-skeleton" aria-hidden="true"><i /><i /><i /><i /><i /></div>
    </el-drawer>

    <el-dialog custom-class="more-dialog" :visible.sync="moreVisible" width="92%" append-to-body>
      <h3>{{ $t("creator.actions") }}</h3>
      <button @click="markNotInterested"><i class="el-icon-remove-outline" /><span>{{ $t("appUi.notInterested") }}<small>{{ $t("appUi.lessSimilar") }}</small></span></button>
      <button @click="blockActiveAuthor"><i class="el-icon-circle-close" /><span>{{ $t("appUi.blockAuthor") }}<small>{{ $t("appUi.blockAuthorTip") }}</small></span></button>
      <button @click="openReport('video', activeTarget && activeTarget.id)"><i class="el-icon-warning-outline" /><span>{{ $t("appUi.reportContent") }}<small>{{ $t("appUi.reportContentTip") }}</small></span></button>
    </el-dialog>

    <el-dialog custom-class="report-dialog" :title="$t('dialog.report')" :visible.sync="reportReasonVisible" width="92%" append-to-body>
      <div class="report-reasons"><button v-for="reason in reportReasons" :key="reason.code" :class="{ active: reportForm.reason === reason.code }" @click="reportForm.reason = reason.code">{{ $t(reason.labelKey) }}</button></div>
      <el-input v-model.trim="reportForm.detail" type="textarea" :rows="3" maxlength="500" show-word-limit :placeholder="$t('appUi.reportDetailPlaceholder')" />
      <el-input v-model.trim="reportForm.evidence" maxlength="1000" :placeholder="$t('appUi.reportEvidencePlaceholder')" />
      <el-button class="report-submit" @click="submitReport">{{ $t("appUi.submitReport") }}</el-button>
    </el-dialog>

    <el-dialog custom-class="share-dialog" :title="$t('dialog.share')" :visible.sync="shareVisible" width="92%" append-to-body>
      <div class="share-options"><button @click="performShare('system')"><i class="el-icon-share" /><span>{{ $t("appUi.systemShare") }}</span></button><button @click="performShare('copy')"><i class="el-icon-link" /><span>{{ $t("appUi.copyLink") }}</span></button><button @click="performShare('internal')"><i class="el-icon-chat-dot-round" /><span>{{ $t("appUi.internalShare") }}</span></button></div>
      <p>{{ $t("appUi.shareChannelNote") }}</p>
    </el-dialog>

    <el-dialog v-if="selectedTip" custom-class="tip-dialog" :title="$t('dialog.confirmTip')" :visible.sync="tipVisible" width="92%" append-to-body>
      <div class="tip-confirm-card"><i class="el-icon-coffee-cup" /><small>{{ $t("appUi.tipTo", { name: authorDetail && authorDetail.author.name }) }}</small><h3>{{ selectedTip.title }}</h3><p>{{ selectedTip.description }}</p><strong>{{ $n(selectedTip.coin_price) }} {{ $t("wallet.coins") }}</strong></div>
      <p class="balance-hint">{{ $t("appUi.currentBalance", { amount: $n(authorShop && authorShop.wallet ? authorShop.wallet.balance : 0) }) }}</p>
      <el-button class="coin-pay-button" :loading="coinPayLoading" @click="confirmTip">{{ $t("appUi.confirmTipPay") }}</el-button>
    </el-dialog>

    <el-dialog v-if="selectedGift" custom-class="tip-dialog gift-dialog" :title="$t('giftUi.dialogTitle')" :visible.sync="giftVisible" width="92%" append-to-body>
      <div class="gift-confirm-card">
        <span>{{ selectedGift.icon }}</span>
        <small>{{ $t("giftUi.toCreator", { name: authorDetail && authorDetail.author.name }) }}</small>
        <h3>{{ selectedGift.name }}</h3>
        <el-input-number v-model="giftQuantity" :min="1" :max="99" :step="1" size="small" />
        <strong>{{ $n(selectedGift.coin_price * giftQuantity) }} {{ $t("wallet.coins") }}</strong>
      </div>
      <p class="balance-hint">{{ $t("appUi.currentBalance", { amount: $n(authorShop && authorShop.wallet ? authorShop.wallet.balance : 0) }) }}</p>
      <el-button class="coin-pay-button" :loading="coinPayLoading" @click="confirmGift">{{ $t("giftUi.confirmPay") }}</el-button>
    </el-dialog>

    <el-dialog v-if="paidDetail" custom-class="paid-dialog" :title="$t('dialog.paidContent')" :visible.sync="paidVisible" width="92%" append-to-body>
      <div :class="['paid-preview', paidDetail.theme]"><span>{{ paidDetail.title.slice(0, 1) }}</span><i :class="paidDetail.accessible ? 'el-icon-unlock' : 'el-icon-lock'" /></div>
      <small class="paid-kind">{{ $t(paidDetail.content_type === 'album' ? "appUi.paidAlbum" : "appUi.paidVideo") }} · {{ paidDetail.visibility === "purchased_only" ? $t("appUi.purchasedVisible") : paidDetail.visibility }}</small>
      <h3>{{ paidDetail.title }}</h3><p class="paid-intro">{{ paidDetail.introduction }}</p>
      <template v-if="paidDetail.accessible">
        <video v-if="paidDetail.content_type === 'video' && paidDetail.assets.length" class="paid-video" :src="paidDetail.assets[0]" controls playsinline />
        <div v-else class="unlocked-album"><figure v-for="(asset, index) in paidDetail.assets" :key="asset"><button type="button" class="unlocked-album-image" @click="openPaidAlbumImage(index)"><img :src="asset" :alt="$t('appUi.albumImageAlt', { title: paidDetail.title, index: index + 1 })" loading="lazy" :draggable="false" /></button><figcaption>{{ $t("appUi.albumImageCaption", { index: index + 1 }) }}</figcaption></figure></div>
        <div class="unlocked-notice"><i class="el-icon-success" /> {{ $t("appUi.purchasedVisible") }}</div>
      </template>
      <div v-else-if="paidDetail.purchased" class="offline-notice"><i class="el-icon-warning-outline" /><span>{{ $t("appUi.purchaseAccessIssue") }}<small>{{ $t("appUi.contactSupport") }}</small></span></div>
      <el-button v-else class="coin-pay-button" :loading="coinPayLoading" @click="buyPaidContent">{{ $t("appUi.payToUnlock", { amount: $n(paidDetail.coin_price) }) }}</el-button>
    </el-dialog>

    <!-- 用户中心的作品使用独立观看层，关闭后仍停留在原内容列表和滚动位置。 -->
    <el-dialog
      v-if="centerVideoDetail"
      custom-class="center-video-dialog"
      :visible.sync="centerVideoVisible"
      width="100%"
      append-to-body
      @closed="closeCenterVideo"
    >
      <div class="center-video-stage">
        <video
          v-if="centerVideoDetail.content_type === 'video'"
          ref="centerVideoPlayer"
          :src="centerVideoDetail.live_source === 'tiktok' ? null : centerVideoDetail.play_url"
          :poster="centerVideoDetail.cover_url"
          controls
          autoplay
          playsinline
          webkit-playsinline
          @play="handlePlay(centerVideoDetail)"
          @pause="handlePause(centerVideoDetail)"
          @timeupdate="handleTimeUpdate(centerVideoDetail, $event)"
          @error="handleVideoError(centerVideoDetail)"
        />
        <image-post-gallery v-else variant="dialog" :images="centerVideoDetail.image_urls" :title="centerVideoDetail.title" :content-id="centerVideoDetail.id" />
        <audio
          v-if="centerVideoDetail.content_type === 'image_text' && centerVideoDetail.music_audio_url"
          ref="centerImageAudioPlayer"
          :src="centerVideoDetail.music_audio_url"
          :muted="muted"
          autoplay
          loop
          preload="metadata"
          @error="handleImageMusicError(centerVideoDetail)"
        />
        <button
          v-if="centerVideoDetail.content_type === 'image_text' && centerVideoDetail.music_audio_url"
          type="button"
          class="center-image-sound"
          :aria-label="muted ? $t('feed.muteOn') : $t('feed.muteOff')"
          @click="toggleMute"
        ><i :class="muted ? 'el-icon-close-notification' : 'el-icon-headset'" /></button>
        <div class="center-video-copy">
          <small>NOW PLAYING</small>
          <h2>{{ centerVideoDetail.title }}</h2>
          <p>{{ centerVideoDetail.description }}</p>
          <div class="center-video-tags"><span v-for="tag in centerVideoDetail.tags" :key="tag">#{{ tag }}</span></div>
          <footer>
            <button class="center-video-author" type="button" :aria-label="$t('a11y.creatorProfile')" @click="openCenterVideoAuthor">
              <platform-avatar :src="centerVideoDetail.author.avatar_url" :alt="centerVideoDetail.author.name" />
              <div><strong>{{ centerVideoDetail.author.name }}</strong><small>@{{ centerVideoDetail.author.account }}</small></div>
            </button>
            <span><i class="el-icon-view" /> {{ formatCount(centerVideoDetail.stats.view) }}</span>
            <span class="center-like-stat"><star-live-heart-icon :selected="Boolean(centerVideoDetail.user_state && centerVideoDetail.user_state.liked)" /> {{ formatCount(centerVideoDetail.stats.like) }}</span>
            <button class="center-video-comments" type="button" :aria-label="$t('a11y.viewComments')" @click="openCenterVideoComments">
              <i class="el-icon-chat-dot-round" /> {{ formatCount(centerVideoDetail.stats.comment) }}
            </button>
          </footer>
        </div>
      </div>
    </el-dialog>

    <el-drawer :title="$t('dialog.wallet')" :visible.sync="walletVisible" direction="rtl" size="100%" custom-class="wallet-drawer" append-to-body>
      <div class="wallet-page">
        <div class="wallet-balance"><small>{{ $t("centerDetail.balance") }}</small><strong>{{ $n(walletData.balance) }}</strong><p>{{ $t("appUi.totalSpent", { amount: $n(walletData.total_spent) }) }}</p></div>
        <el-tabs v-model="walletTab" @tab-click="handleWalletTabClick">
          <el-tab-pane :label="$t('wallet.transactions')" name="transactions">
            <article v-for="item in walletTransactions" :key="item.id" class="ledger-item"><div><strong>{{ item.description }}</strong><small>{{ $d(item.create_time, { dateStyle: "medium", timeStyle: "short" }) }} · {{ item.trade_no }}</small></div><span>{{ $n(item.amount) }}<small>{{ $t("appUi.balanceAfter", { amount: $n(item.balance_after) }) }}</small></span></article>
            <p v-if="walletPagination.transactions.loading && !walletTransactions.length" class="wallet-empty"><i class="el-icon-loading" /> {{ $t("common.loading") }}</p>
            <p v-else-if="walletPagination.transactions.loaded && !walletTransactions.length" class="wallet-empty">{{ $t("appUi.noTransactions") }}</p>
            <button v-if="walletPagination.transactions.has_more" class="wallet-load-more" type="button" :disabled="walletPagination.transactions.loading" @click="loadMoreWalletList('transactions')"><i v-if="walletPagination.transactions.loading" class="el-icon-loading" />{{ $t(walletPagination.transactions.loading ? "appUi.loadingMore" : "discoverLibrary.loadMore") }}</button>
          </el-tab-pane>
          <el-tab-pane :label="$t('wallet.purchases')" name="purchases">
            <article v-for="item in walletPurchases" :key="item.id" class="purchase-item" @click="openPaidContent({ id: item.content_id })"><span :class="item.theme">{{ item.title.slice(0, 1) }}</span><div><strong>{{ item.title }}</strong><small>{{ item.author_name }} · {{ $n(item.coin_amount) }} {{ $t("wallet.coins") }}</small><em>{{ $t(item.listed ? "appUi.viewContent" : "appUi.delistedAccessible") }}</em></div></article>
            <p v-if="walletPagination.purchases.loading && !walletPurchases.length" class="wallet-empty"><i class="el-icon-loading" /> {{ $t("common.loading") }}</p>
            <p v-else-if="walletPagination.purchases.loaded && !walletPurchases.length" class="wallet-empty">{{ $t("appUi.noPurchases") }}</p>
            <button v-if="walletPagination.purchases.has_more" class="wallet-load-more" type="button" :disabled="walletPagination.purchases.loading" @click="loadMoreWalletList('purchases')"><i v-if="walletPagination.purchases.loading" class="el-icon-loading" />{{ $t(walletPagination.purchases.loading ? "appUi.loadingMore" : "discoverLibrary.loadMore") }}</button>
          </el-tab-pane>
        </el-tabs>
      </div>
    </el-drawer>
    <chat-share-picker :visible="chatShareVisible" :content="chatShareTarget" @close="closeChatShare" @sent="handleChatShareSent" />
    <transition name="dynamic-island">
      <dynamic-island-notice v-if="islandCurrent" :key="islandCurrent.key" :item="islandCurrent" @select="selectIslandNotice" @dismiss="dismissIslandNotice" />
    </transition>
    <theme-notice />
    <pwa-controller ref="pwaController" />
    <app-splash v-if="!paymentReturnVisible" :ready="appReady" />
    </template>
  </main>
</template>

<script>
import { getProfile, loginAccount, logoutAccount, passwordLogin, registerAccount, resetPassword, sendEmailCode } from "./api/auth"
import { addComment, addReport, addShare, addWatch, deleteComment, getAuthor, getChannels, getComments, getFeed, getLiveStream, getVideoDetail, updateAction, updateBlock, updateCommentLike, updateFollow } from "./api/content"
import { addGift, addPurchase, addTip, getCoinTransactions, getCreatorShop, getPaidContent, getPurchases, getWallet } from "./api/monetization"
import { getNotificationPopups, getNotificationUnread, markNotificationRead } from "./api/notification"
import { addDiscoveryEvent } from "./api/discover"
import { privacyAccountDeleteCancel, privacyRecoveryCodeSend } from "./api/privacy"
import { joinFanGroup } from "./api/fanGroup"
import { clearAuthToken, getAuthToken, setAuthToken } from "./utils/auth"
import { avatarUrl } from "./utils/media"
import { updateSeo } from "./utils/seo"
import behaviorAnalytics from "./utils/behaviorAnalytics"
import NotificationCenter from "./components/NotificationCenter.vue"
import UserPublishComposer from "./components/UserPublishComposer.vue"
import DiscoverPage from "./components/DiscoverPage.vue"
import LocaleSwitcher from "./components/LocaleSwitcher.vue"
import SearchDiscovery from "./components/SearchDiscovery.vue"
import ThemeNotice from "./components/ThemeNotice.vue"
import UserCenter from "./components/UserCenter.vue"
import AccountSettings from "./components/AccountSettings.vue"
import LegalDocumentViewer from "./components/LegalDocumentViewer.vue"
import LiveRoom from "./components/LiveRoom.vue"
import AvatarWithFrame from "./components/AvatarWithFrame.vue"
import ChatSharePicker from "./components/ChatSharePicker.vue"
import DynamicIslandNotice from "./components/DynamicIslandNotice.vue"
import ImagePostGallery from "./components/ImagePostGallery.vue"
import ImageViewer from "./components/ImageViewer.vue"
import StarLiveHeartIcon from "./components/StarLiveHeartIcon.vue"
import PWAController from "./components/PWA/PWAController.vue"
import AppSplash from "./components/PWA/AppSplash.vue"
import PaymentResult from "./components/PaymentResult.vue"
import GameRechargePage from "./components/GameRechargePage.vue"
import GameRechargeResult from "./components/GameRechargeResult.vue"
import { openImageViewer } from "./utils/imageViewer"
import { claimPendingPaymentResume } from "./utils/paymentCheckout"
import { isStandalone } from "./utils/pwa"
import { friendUnreadTotal } from "./utils/notificationUnread"

export default {
  name: "App",
  components: { AccountSettings, AppSplash, AvatarWithFrame, ChatSharePicker, DiscoverPage, DynamicIslandNotice, GameRechargePage, GameRechargeResult, ImagePostGallery, ImageViewer, LegalDocumentViewer, LiveRoom, LocaleSwitcher, NotificationCenter, PWAController, PaymentResult, SearchDiscovery, StarLiveHeartIcon, ThemeNotice, UserCenter, UserPublishComposer },
  data() {
    const standalonePath = window.location.pathname.replace(/\/$/, "")
    return {
      appReady: false, gameRechargePageVisible: standalonePath === "/game-recharge", gameRechargeResultVisible: standalonePath === "/game-recharge/result", gameRechargeStandaloneVisible: ["/game-recharge", "/game-recharge/result"].includes(standalonePath), paymentReturnVisible: standalonePath === "/payment/result", channels: [], activeChannel: "recommend", videos: [], currentIndex: 0, feedLoading: false, feedLoadingMore: false, feedHasMore: true, feedRequestId: 0, channelFeedStates: {}, feedStartupRetrying: false, feedStartupRetryTimer: 0, emptyText: "",
      // 媒体默认开启声音；浏览器仍可按自身自动播放策略阻止首次有声播放。
      muted: false, weakNetwork: false, scrollTimer: null, feedTapTimer: null, feedTapVideoId: 0, currentUser: null, pendingAction: null, notificationPollTimer: 0, notificationPollingBusy: false,
      islandCurrent: null, islandQueue: [], islandSeenKeys: [], islandLiveCursor: 0, islandChatCursor: 0, islandUserId: 0, islandAdvanceTimer: 0,
      commentsVisible: false, comments: [], commentsEnabled: true, commentText: "", replyingComment: null, focusedCommentId: 0, activeTarget: null, searchVisible: false, discoverVisible: false, discoverVisited: false, profileVisible: false, profileVisited: false, messageVisible: false, messageVisited: false, notificationUnread: 0, activeRootTab: "home", settingsVisible: false, settingsInitialPanel: "", settingsGuildRequestId: 0, settingsSupportTicketId: 0, legalVisible: false, legalType: "terms",
      authorVisible: false, authorDetail: null, authorLoading: false, authorWorkTab: "video", authorWorksLoading: false, authorShopLoading: false, authorShopCache: {}, authorRequestId: 0, fanGroupJoining: false, moreVisible: false, reportReasonVisible: false, reportTarget: null,
      reportForm: { reason: "spam", detail: "", evidence: "" }, shareVisible: false, chatShareVisible: false, chatShareTarget: null,
      studioVisible: false, studioAccessLoading: false, publishVisible: false,
      liveRoomVideo: null, liveRoomQueue: [], liveRoomIndex: -1,
      liveRoomQueueLoading: false, liveRoomSwitching: false,
      liveRoomTransitionName: "live-room-switch-next", liveRoomSwitchTimer: null,
      liveRoomQueueRequestId: 0,
      miniMusic: null,
      authorShop: null, selectedGift: null, giftVisible: false, giftQuantity: 1, giftRequestNo: "", selectedTip: null, tipVisible: false, paidDetail: null, paidVisible: false, coinPayLoading: false,
      centerVideoDetail: null, centerVideoVisible: false,
      walletVisible: false, walletData: {}, walletTransactions: [], walletPurchases: [], walletTab: "transactions",
      walletPagination: {
        transactions: { page: 0, limit: 20, total: 0, has_more: true, loaded: false, loading: false },
        purchases: { page: 0, limit: 20, total: 0, has_more: true, loaded: false, loading: false }
      },
      authVisible: false, authMode: "login", loginMethod: "password", authLoading: false, codeSending: false, countdown: 0, countdownTimer: null,
      authForm: { email: "", code: "", password: "", passwordConfirmation: "", remember: true, agreement: false, privacy: false },
      reportReasons: [{ code: "spam", labelKey: "appUi.reasonSpam" }, { code: "illegal", labelKey: "appUi.reasonIllegal" }, { code: "infringement", labelKey: "appUi.reasonInfringement" }, { code: "violence", labelKey: "appUi.reasonViolence" }, { code: "other", labelKey: "appUi.reasonOther" }]
    }
  },
  computed: {
    activeVideo() { return this.videos[this.currentIndex] || null },
    isLiveChannel() {
      const channel = this.channels.find(item => item.code === this.activeChannel)
      return Boolean(channel && channel.type === "live")
    },
    threadedComments() {
      // 接口保留扁平数据结构，这里按 parent_id 组织为深度优先顺序，保证每条子评论紧跟对应父评论。
      const comments = this.comments.slice()
      const commentIds = new Set(comments.map(comment => Number(comment.id)))
      const childrenByParent = new Map()
      const roots = []

      comments.forEach(comment => {
        const parentId = Number(comment.parent_id) || 0
        if (parentId === 0 || !commentIds.has(parentId)) {
          // 父评论已删除或不在本次结果中时仍展示该评论，避免内容无故消失。
          roots.push(comment)
          return
        }
        if (!childrenByParent.has(parentId)) childrenByParent.set(parentId, [])
        childrenByParent.get(parentId).push(comment)
      })

      // 父评论按最新优先；同一父评论的回复按发布时间正序，阅读上下文更自然。
      roots.sort((left, right) => Number(right.id) - Number(left.id))
      childrenByParent.forEach(children => children.sort((left, right) => Number(left.id) - Number(right.id)))

      const ordered = []
      const visited = new Set()
      const appendThread = comment => {
        const commentId = Number(comment.id)
        if (visited.has(commentId)) return
        visited.add(commentId)
        ordered.push(comment)
        ;(childrenByParent.get(commentId) || []).forEach(appendThread)
      }
      roots.forEach(appendThread)
      // 防御异常的循环 parent_id 数据，确保评论仍可见且不会递归死循环。
      comments.forEach(appendThread)
      return ordered
    },
    needsCode() { return ["register", "reset", "restore"].includes(this.authMode) || (this.authMode === "login" && this.loginMethod === "code") },
    needsPassword() { return this.authMode === "register" || this.authMode === "reset" || (this.authMode === "login" && this.loginMethod === "password") },
    needsPasswordConfirmation() { return this.authMode === "register" || this.authMode === "reset" },
    passwordAccountMode() { return this.authMode === "login" && this.loginMethod === "password" },
    authAccountLabel() { return this.$t(this.passwordAccountMode ? "appUi.account" : "appUi.email") },
    authAccountPlaceholder() { return this.passwordAccountMode ? this.$t("appUi.accountPlaceholder") : "name@example.com" },
    authRules() { return { email: this.passwordAccountMode ? [{ required: true, message: this.$t("appUi.accountRequired"), trigger: "blur" }] : [{ required: true, message: this.$t("appUi.emailRequired"), trigger: "blur" }, { type: "email", message: this.$t("appUi.emailInvalid"), trigger: "blur" }], code: [], password: [], passwordConfirmation: [] } },
    authTitle() { if (this.authMode === "register") return this.$t("brand.join"); if (this.authMode === "reset") return this.$t("appUi.setNewPassword"); if (this.authMode === "restore") return this.$t("privacyManagement.recoveryTitle"); return this.$t("appUi.loginToContinue") },
    authDescription() { if (this.authMode === "restore") return this.$t("privacyManagement.recoveryDescription"); if (this.authMode === "reset") return this.$t("appUi.verifyByEmail"); if (this.authMode === "register") return this.$t("appUi.createPasswordOrCode"); return this.$t(this.loginMethod === "password" ? "appUi.safePasswordLogin" : "appUi.safeCodeLogin") },
    authSubmitText() { if (this.authMode === "restore") return this.$t("privacyManagement.recoverySubmit"); if (this.authMode === "register") return this.$t("appUi.registerExplore"); if (this.authMode === "reset") return this.$t("appUi.setPasswordLogin"); return this.$t("appUi.loginContinue") },
    miniMusicProgress() {
      if (!this.miniMusic || !Number(this.miniMusic.duration)) return 0
      return Math.min(100, Math.max(0, Number(this.miniMusic.current_time) / Number(this.miniMusic.duration) * 100))
    }
  },
  methods: {
    avatarUrl,
    handlePendingPaymentResume() {
      if (document.hidden || this.paymentReturnVisible || !isStandalone() || !claimPendingPaymentResume()) return
      this.paymentReturnVisible = true
    },
    closePaymentResult() {
      this.paymentReturnVisible = false
    },
    handlePaymentSettled() {
      this.refreshNotificationInbox()
      if (this.$refs.userCenter && this.profileVisible) this.$refs.userCenter.loadWallet()
      if (this.$refs.liveRoom) this.$refs.liveRoom.loadShop()
    },
    openPaidAlbumImage(index) { if (this.paidDetail) openImageViewer(this.paidDetail.assets, index, { title: this.paidDetail.title }) },
    async initialize() {
      this.detectNetwork()
      // 首页推荐不依赖个人资料或频道接口。先展示内容，慢接口在后台补齐，避免首屏被无关请求阻塞。
      const profileTask = this.loadProfile().catch(() => {})
      const channelTask = this.loadChannels().catch(() => {})
      try {
        await this.loadFeed()
        await this.openSharedTargetFromUrl()
      } catch (error) {
        // 请求层已显示本地化主题提示；生命周期内接管异常，避免 Vue 开发错误遮罩覆盖页面。
        this.emptyText = this.$t("feed.preparing")
      } finally {
        // 首屏请求无论成功或失败都结束启动遮罩；网络异常交给页面重试和离线提示处理。
        this.appReady = true
      }
      await Promise.all([profileTask, channelTask])
    },
    handleNetworkRestored() {
      if ((!this.videos.length || this.feedStartupRetrying) && !this.feedLoading) this.loadFeed({ startupRetry: true }).catch(() => {})
    },
    async loadChannels() {
      const res = await getChannels()
      this.channels = res.data.list
      if (!this.channels.some(item => item.code === this.activeChannel) && this.channels.length) this.activeChannel = this.channels[0].code
    },
    scheduleStartupFeedRetry() {
      if (this.isLiveChannel || this.feedStartupRetryTimer || this.feedStartupRetrying) return
      this.feedStartupRetrying = true
      this.feedStartupRetryTimer = window.setTimeout(() => {
        this.feedStartupRetryTimer = 0
        this.feedStartupRetrying = false
        this.loadFeed({ startupRetry: true }).catch(() => {})
      }, 800)
    },
    saveActiveChannelFeed() {
      if (!this.activeChannel || !this.videos.length) return
      this.$set(this.channelFeedStates, this.activeChannel, {
        videos: this.videos,
        currentIndex: this.currentIndex,
        hasMore: this.feedHasMore
      })
    },
    applyFeed(rows, { preserveVideoId = 0, preserveIndex = 0 } = {}) {
      this.videos = (rows || []).map(item => item.feed_kind === "discovery" ? { ...item, impression_tracked: false } : this.decorateVideo(item))
      const matchedIndex = preserveVideoId > 0 ? this.videos.findIndex(item => Number(item.id) === Number(preserveVideoId)) : -1
      this.currentIndex = Math.max(0, Math.min(this.videos.length - 1, matchedIndex >= 0 ? matchedIndex : preserveIndex))
      this.emptyText = this.$t("feed.empty")
      this.$nextTick(() => {
        const feed = this.$refs.feed
        if (feed && this.currentIndex > 0) feed.scrollTo({ top: this.currentIndex * feed.clientHeight })
        this.playCurrent({ forceAutoplay: true })
      })
    },
    async loadFeed({ startupRetry = false, background = false, channel = this.activeChannel } = {}) {
      const requestId = ++this.feedRequestId
      if (!background) this.feedLoading = true
      if (!background && channel === this.activeChannel) this.pauseAll()
      try {
        const res = await getFeed({ channel, limit: 10, discovery_exclude: this.discoveryFeedExclusions(), exclude_ids: this.feedExcludeIds(channel).join(","), previous_author_id: 0 })
        if (requestId !== this.feedRequestId || channel !== this.activeChannel) return
        const active = this.activeVideo
        this.applyFeed(res.data.list, { preserveVideoId: active ? active.id : 0, preserveIndex: this.currentIndex })
        this.feedHasMore = this.videos.length >= 10
        this.saveActiveChannelFeed()
        if (this.videos.length === 0 && !startupRetry && !background) this.scheduleStartupFeedRetry()
      } catch (error) {
        if (!startupRetry && !background) this.scheduleStartupFeedRetry()
        throw error
      } finally {
        if (!background && requestId === this.feedRequestId && channel === this.activeChannel) this.feedLoading = false
      }
    },
    async loadMoreFeed() {
      if (this.feedLoadingMore || !this.feedHasMore || this.videos.length === 0) return
      this.feedLoadingMore = true
      try {
        const excludeIds = this.feedExcludeIds(this.activeChannel)
        const tail = [...this.videos].reverse().find(item => item.feed_kind !== "discovery" && item.author)
        const res = await getFeed({ channel: this.activeChannel, limit: 10, discovery_exclude: this.discoveryFeedExclusions(), exclude_ids: excludeIds.join(","), previous_author_id: Number(tail && tail.author.id || 0) })
        const existing = new Set(this.videos.map(item => `${item.feed_kind || "video"}:${item.id}`))
        const appended = (res.data.list || [])
          .filter(item => !existing.has(`${item.feed_kind || "video"}:${item.id}`))
          .map(item => item.feed_kind === "discovery" ? { ...item, impression_tracked: false } : this.decorateVideo(item))
        if (appended.length) this.videos.push(...appended)
        this.feedHasMore = (res.data.list || []).length >= 10 && appended.length > 0
        this.saveActiveChannelFeed()
      } finally {
        this.feedLoadingMore = false
      }
    },
    decorateVideo(video) {
      const isLive = video.content_type === "live"
      const isImageText = video.content_type === "image_text"
      return { ...video, image_urls: Array.isArray(video.image_urls) ? video.image_urls : [], music_audio_url: String(video.music_audio_url || ""), play_url: this.weakNetwork ? video.low_video_url : video.video_url, load_state: video.available ? (isLive || isImageText ? "ready" : "loading") : "unavailable", playing: false, user_paused: false, progress: 0, view_started: false, completed: false, last_report_second: -1, live_stream_ready: video.live_source !== "tiktok", live_stream_loading: false, music_playback_error: false, like_burst: false, like_burst_key: 0, action_loading: { like: false, favorite: false } }
    },
    feedSeenStorageKey() { return "starlive_recommendation_seen_v1" },
    feedSeenRows() {
      const now = Date.now()
      const expiresAt = now - 90 * 24 * 60 * 60 * 1000
      let rows = []
      try {
        const stored = JSON.parse(localStorage.getItem(this.feedSeenStorageKey()) || "[]")
        rows = Array.isArray(stored) ? stored : []
      } catch (error) { /* 本地存储不可用时仍由已登录用户的服务端记录去重。 */ }
      const valid = rows
        .map(item => ({ id: Number(item.id), seenAt: Number(item.seenAt) }))
        .filter(item => Number.isInteger(item.id) && item.id > 0 && Number.isFinite(item.seenAt) && item.seenAt >= expiresAt)
        .sort((left, right) => right.seenAt - left.seenAt)
        .slice(0, 720)
      try { localStorage.setItem(this.feedSeenStorageKey(), JSON.stringify(valid)) } catch (error) { /* 配额或隐私模式不阻断推荐流。 */ }
      return valid
    },
    feedSeenVideoIds() {
      return this.feedSeenRows().map(item => item.id)
    },
    markRecommendationSeen(video) {
      if (!video || video.feed_kind === "discovery" || video.content_type === "live") return
      const id = Number(video.id)
      if (!Number.isInteger(id) || id <= 0) return
      const seenAt = Date.now()
      const rows = this.feedSeenRows().filter(item => item.id !== id)
      rows.unshift({ id, seenAt })
      try { localStorage.setItem(this.feedSeenStorageKey(), JSON.stringify(rows.slice(0, 720))) } catch (error) { /* 本地存储失败不影响播放。 */ }
    },
    feedExcludeIds(channel = this.activeChannel) {
      const visibleIds = this.videos
        .filter(item => item.feed_kind !== "discovery")
        .map(item => Number(item.id))
        .filter(id => Number.isInteger(id) && id > 0)
      const seenIds = channel === "recommend" ? this.feedSeenVideoIds() : []
      // 先保留当前屏幕已展示内容，再按最近观看时间补齐；不能因截断而丢掉刚看过的作品。
      return Array.from(new Set([...visibleIds, ...seenIds])).slice(0, 360)
    },
    switchChannel(channel) {
      if (channel.login_required && !this.currentUser) { this.showAuth("login", { action: "channel", code: channel.code }); return }
      this.activeRootTab = "home"
      if (this.activeChannel === channel.code) return
      this.saveActiveChannelFeed()
      this.activeChannel = channel.code
      this.feedLoadingMore = false
      const cached = this.channelFeedStates[channel.code]
      if (cached && cached.videos && cached.videos.length) {
        this.videos = cached.videos
        this.currentIndex = cached.currentIndex || 0
        this.feedHasMore = cached.hasMore !== false
        this.$nextTick(() => this.playCurrent({ forceAutoplay: true }))
        this.loadFeed({ background: true, channel: channel.code }).catch(() => {})
        return
      }
      this.videos = []
      this.currentIndex = 0
      this.feedHasMore = true
      this.loadFeed({ channel: channel.code })
    },
    handleDiscover() {
      this.closeUserPublish()
      this.pauseAll()
      this.closeCenterSubpages()
      this.studioVisible = false
      this.profileVisible = false
      this.messageVisible = false
      this.discoverVisited = true
      this.discoverVisible = true
      this.activeRootTab = "discover"
      this.$nextTick(() => {
        if (this.$refs.discoverPage) this.$refs.discoverPage.activate()
      })
    },
    openDecorationStore() {
      this.handleDiscover()
      this.$nextTick(async () => {
        const page = this.$refs.discoverPage
        if (!page) return
        await page.initialize()
        await page.switchChannel("decoration")
      })
    },
    handleMusicStateChange(state) {
      this.miniMusic = state ? { ...state, track: { ...state.track } } : null
    },
    openMiniMusicLyrics() {
      if (!this.miniMusic || !this.miniMusic.track) return
      this.searchVisible = false
      this.handleDiscover()
      this.$nextTick(() => {
        if (this.$refs.discoverPage && this.$refs.discoverPage.expandMusicPlayer) this.$refs.discoverPage.expandMusicPlayer()
      })
    },
    toggleMiniMusic() {
      if (this.$refs.discoverPage && this.$refs.discoverPage.toggleMusicPlayback) this.$refs.discoverPage.toggleMusicPlayback()
    },
    closeMiniMusic() {
      const page = this.$refs.discoverPage
      if (page && page.closeMusicPlayer) {
        page.closeMusicPlayer()
        return
      }
      this.miniMusic = null
    },
    hideMiniMusicCover(event) {
      event.target.style.display = "none"
    },
    showHome() {
      this.closeUserPublish()
      this.closeCenterSubpages()
      this.studioVisible = false
      this.activeRootTab = "home"
      if (!this.profileVisible && !this.messageVisible && !this.discoverVisible) return
      this.profileVisible = false; this.messageVisible = false; this.discoverVisible = false
      this.$nextTick(() => this.playCurrent())
    },
    openSearch() { this.pauseAll(); this.searchVisible = true },
    closeSearch() { this.searchVisible = false; this.$nextTick(() => this.playCurrent()) },
    requestSearchLogin(person) {
      const userId = Number(person && person.user_id || 0)
      if (userId > 0) this.showAuth("login", { action: "search_follow", userId })
      else this.showAuth("login")
    },
    openSearchUser(person) {
      if (!person) return
      const authorId = Number(person.author_id || 0)
      const userId = Number(person.user_id || 0)
      if (authorId > 0) { this.openAuthor(authorId); return }
      if (userId <= 0) return
      if (!this.currentUser) { this.showAuth("login"); return }
      this.searchVisible = false
      this.pauseAll(); this.closeCenterSubpages(); this.discoverVisible = false; this.messageVisible = false; this.studioVisible = false
      this.profileVisited = true; this.profileVisible = true; this.activeRootTab = "profile"
      this.$nextTick(() => {
        if (this.$refs.userCenter && this.$refs.userCenter.openMemberProfile) this.$refs.userCenter.openMemberProfile(userId, false).catch(() => {})
      })
    },
    selectSearchVideo(videoId, done) {
      // 搜索结果使用独立观看层，不重载推荐流、不改变首页位置，也不把作品插入推荐列表。
      return this.openDirectVideo(Number(videoId)).then(video => {
        if (video) this.searchVisible = false
        return video
      }).finally(() => {
        if (typeof done === "function") done()
      })
    },
    feedIndexFromScrollPosition() {
      const feed = this.$refs.feed
      if (!feed || !feed.clientHeight) return -1
      return Math.max(0, Math.min(this.videos.length - 1, Math.round(feed.scrollTop / feed.clientHeight)))
    },
    activateFeedIndex(index) {
      if (index < 0 || index >= this.videos.length || index === this.currentIndex) return
      this.currentIndex = index
      this.$nextTick(() => {
        const active = this.activeVideo
        this.pauseInactiveMedia(active && active.id)
        this.playCurrent({ forceAutoplay: true })
      })
    },
    handleFeedScroll() {
      // 滚动惯性会连续触发。不要在这里暂停全部媒体，否则新一条进入视口时
      // 会经历“暂停 -> 等待定时器 -> 再播放”的可感知卡顿。
      this.activateFeedIndex(this.feedIndexFromScrollPosition())
      window.clearTimeout(this.scrollTimer)
      this.scrollTimer = window.setTimeout(() => {
        this.activateFeedIndex(this.feedIndexFromScrollPosition())
        if (this.videos.length - this.currentIndex <= 4) this.loadMoreFeed()
      }, 80)
    },
    isMediaWindow(index) { return Math.abs(index - this.currentIndex) <= 1 },
    videoPreloadMode(video, index) {
      if (!video || video.content_type !== "video" || video.live_source === "tiktok") return "none"
      if (index === this.currentIndex) return "auto"
      // 下一条优先准备可播放缓冲；弱网只取元数据，避免抢占正在播放的带宽。
      return index === this.currentIndex + 1 && !this.weakNetwork ? "auto" : "none"
    },
    audioPreloadMode(index) { return index === this.currentIndex ? "auto" : "none" },
    videoElement(videoId) {
      const refs = this.$refs.videoPlayers || []
      const list = Array.isArray(refs) ? refs : [refs]
      return list.find(element => Number(element.dataset.videoId) === Number(videoId))
    },
    imagePostAudioElement(videoId) {
      const refs = this.$refs.imagePostAudioPlayers || []
      const list = Array.isArray(refs) ? refs : [refs]
      return list.find(element => Number(element.dataset.videoId) === Number(videoId))
    },
    centerMediaElement() {
      return this.$refs.centerVideoPlayer || this.$refs.centerImageAudioPlayer || null
    },
    pauseAmbientMusic() {
      const discoverPage = this.$refs.discoverPage
      if (this.miniMusic && this.miniMusic.playing && discoverPage && discoverPage.toggleMusicPlayback) {
        discoverPage.toggleMusicPlayback()
      }
    },
    async playCurrent({ forceAutoplay = false } = {}) {
      const video = this.activeVideo
      if (video && video.feed_kind === "discovery") {
        this.markDiscoveryFeedSeen(video)
        return
      }
      // 进入当前屏即写入本地曝光历史；不能依赖媒体 play 事件，否则图片帖、弱网和
      // iOS 自动播放失败的内容在重新打开应用后仍会再次出现。
      this.markRecommendationSeen(video)
      if (!video || !video.available || video.load_state === "error") return
      if (forceAutoplay) video.user_paused = false
      if (video.user_paused) return false
      if (video.content_type === "image_text") {
        const audio = this.imagePostAudioElement(video.id)
        if (!audio || video.music_playback_error) return
        this.pauseAmbientMusic()
        await this.playCurrentMedia(video, audio)
        return
      }
      if (video.content_type !== "video") return
      const element = this.videoElement(video.id)
      if (!element) return
      element.muted = this.muted
      if (video.live_source === "tiktok" && !video.live_stream_ready) {
        const ready = await this.prepareLiveStream(video, element)
        if (!ready) return
      }
      await this.playCurrentMedia(video, element)
    },
    async playCurrentMedia(video, element) {
      if (!video || !element) return false
      element.muted = this.muted
      try {
        await element.play()
        return true
      } catch (error) {
        // 有声自动播放被浏览器拒绝时保持用户的声音选择，暂停并等待用户轻触恢复；
        // 禁止为了继续自动播放而静音，避免连续刷推荐时无提示地丢失声音。
        if (!video.user_paused) video.user_paused = true
        video.playing = false
        return false
      }
    },
    async prepareLiveStream(video, element) {
      if (!video || !element || video.live_stream_loading) return false
      video.live_stream_loading = true
      video.load_state = "loading"
      try {
        const res = await getLiveStream({ id: video.id })
        const stream = res.data || {}
        const supportsNativeHls = Boolean(stream.hls_url && element.canPlayType("application/vnd.apple.mpegurl"))
        // Safari/iOS 优先原生 HLS；其他浏览器按参考实现优先尝试 FLV，并保留 HLS 降级。
        const url = supportsNativeHls
          ? stream.hls_url
          : (stream.flv_url || stream.hls_url || stream.fallback_url)
        if (!url) {
          video.load_state = "error"
          return false
        }
        video.play_url = url
        video.live_stream_ready = true
        element.src = url
        element.load()
        return true
      } catch (error) {
        video.load_state = "error"
        if (error.responseData && Number(error.responseData.code) === 410) {
          video.available = false
        }
        return false
      } finally {
        video.live_stream_loading = false
      }
    },
    pauseAll() {
      const mediaRefs = [this.$refs.videoPlayers || [], this.$refs.imagePostAudioPlayers || []]
      mediaRefs.forEach(refs => {
        const list = Array.isArray(refs) ? refs : [refs]
        list.forEach(element => element && element.pause())
      })
    },
    pauseInactiveMedia(activeVideoId) {
      const activeId = Number(activeVideoId)
      const mediaRefs = [this.$refs.videoPlayers || [], this.$refs.imagePostAudioPlayers || []]
      mediaRefs.forEach(refs => {
        const list = Array.isArray(refs) ? refs : [refs]
        list.forEach(element => {
          if (element && Number(element.dataset.videoId) !== activeId) element.pause()
        })
      })
    },
    openLiveRoom(video) {
      if (!video || !video.available) return
      this.pauseAll()
      this.liveRoomVideo = video
      this.liveRoomQueue = [video]
      this.liveRoomIndex = 0
      this.loadLiveRoomQueue(video.id)
    },
    async loadLiveRoomQueue(selectedId) {
      const requestId = ++this.liveRoomQueueRequestId
      this.liveRoomQueueLoading = true
      try {
        const response = await getFeed({ channel: "live", limit: 20 })
        if (requestId !== this.liveRoomQueueRequestId) return
        const selected = this.liveRoomVideo && Number(this.liveRoomVideo.id) === Number(selectedId)
          ? this.liveRoomVideo
          : this.liveRoomQueue.find(item => Number(item.id) === Number(selectedId))
        const rows = (response.data.list || [])
          .filter(item => item.content_type === "live" && Number(item.live_status) === 1 && item.available)
          .map(item => this.decorateVideo(item))
        const merged = []
        const seen = new Set()
        const queueSource = rows.some(item => Number(item.id) === Number(selectedId))
          ? rows
          : [selected, ...rows]
        ;queueSource.forEach(item => {
          if (!item || seen.has(Number(item.id))) return
          seen.add(Number(item.id))
          merged.push(item)
        })
        if (!this.liveRoomVideo || Number(this.liveRoomVideo.id) !== Number(selectedId)) return
        this.liveRoomQueue = merged
        this.liveRoomIndex = Math.max(0, merged.findIndex(item => Number(item.id) === Number(selectedId)))
      } catch (error) {
        // 当前直播继续播放；队列加载失败只影响上下切换，不弹出全局系统错误。
      } finally {
        if (requestId === this.liveRoomQueueRequestId) this.liveRoomQueueLoading = false
      }
    },
    switchLiveRoom(direction) {
      if (this.liveRoomSwitching || !this.liveRoomVideo) return
      if (this.liveRoomQueueLoading) {
        this.$notice.info(this.$t("liveRoom.queueLoading"))
        return
      }
      const offset = Number(direction) > 0 ? 1 : -1
      const targetIndex = this.liveRoomIndex + offset
      if (targetIndex < 0 || targetIndex >= this.liveRoomQueue.length) {
        this.$notice.info(this.$t(offset > 0 ? "liveRoom.noNextLive" : "liveRoom.noPreviousLive"))
        return
      }

      this.liveRoomSwitching = true
      this.liveRoomTransitionName = offset > 0 ? "live-room-switch-next" : "live-room-switch-previous"
      this.liveRoomIndex = targetIndex
      this.liveRoomVideo = this.liveRoomQueue[targetIndex]
      window.clearTimeout(this.liveRoomSwitchTimer)
      this.liveRoomSwitchTimer = window.setTimeout(() => {
        this.liveRoomSwitching = false
      }, 360)
    },
    closeLiveRoom() {
      this.liveRoomQueueRequestId += 1
      this.liveRoomQueueLoading = false
      // 退出不是切换到上一/下一场直播。使用淡出，避免复用切场的向上离场动画，
      // 与底层首页恢复播放叠加后看起来像整页卡顿上移。
      this.liveRoomTransitionName = "live-room-close"
      this.liveRoomVideo = null
      this.liveRoomQueue = []
      this.liveRoomIndex = -1
      this.liveRoomSwitching = false
      window.clearTimeout(this.liveRoomSwitchTimer)
      this.$nextTick(() => this.playCurrent())
    },
    handleLiveEnded() {
      const endedId = Number(this.liveRoomVideo && this.liveRoomVideo.id || 0)
      const remaining = this.liveRoomQueue.filter(item => Number(item.id) !== endedId)
      this.$notice.warning(this.$t("errors.LIVE.ENDED"))
      if (remaining.length) {
        const targetIndex = Math.min(Math.max(0, this.liveRoomIndex), remaining.length - 1)
        this.liveRoomQueue = remaining
        this.liveRoomIndex = targetIndex
        this.liveRoomTransitionName = "live-room-switch-next"
        this.liveRoomVideo = remaining[targetIndex]
      } else {
        this.closeLiveRoom()
      }
      if (this.isLiveChannel) this.loadFeed()
    },
    openLiveRoomAuthor(authorId) {
      this.liveRoomQueueRequestId += 1
      this.liveRoomQueueLoading = false
      this.liveRoomVideo = null
      this.liveRoomQueue = []
      this.liveRoomIndex = -1
      this.liveRoomSwitching = false
      window.clearTimeout(this.liveRoomSwitchTimer)
      this.openAuthor(Number(authorId))
    },
    openLiveRoomMessage(target) {
      this.closeLiveRoom()
      this.openDirectChat(target)
    },
    openLiveRoomProfile(person) {
      if (!person) return
      const authorId = Number(person.author_id || 0)
      const userId = Number(person.user_id || 0)
      if (authorId > 0) {
        this.openLiveRoomAuthor(authorId)
        return
      }
      if (userId <= 0 || !this.currentUser) return

      this.liveRoomQueueRequestId += 1
      this.liveRoomQueueLoading = false
      this.liveRoomVideo = null
      this.liveRoomQueue = []
      this.liveRoomIndex = -1
      this.liveRoomSwitching = false
      window.clearTimeout(this.liveRoomSwitchTimer)
      this.pauseAll()
      this.closeCenterSubpages()
      this.discoverVisible = false
      this.messageVisible = false
      this.studioVisible = false
      this.profileVisited = true
      this.profileVisible = true
      this.activeRootTab = "profile"
      this.$nextTick(() => {
        if (this.$refs.userCenter && this.$refs.userCenter.openMemberProfile) {
          this.$refs.userCenter.openMemberProfile(userId, false).catch(() => {})
        }
      })
    },
    togglePlayback(video) {
      const element = this.videoElement(video.id)
      if (!element) return
      if (element.paused) {
        video.user_paused = false
        element.play().catch(() => { video.user_paused = true })
      } else {
        video.user_paused = true
        element.pause()
      }
    },
    handleVideoSurfaceClick(video) {
      if (!video || video.content_type !== "video") return
      if (this.feedTapTimer && Number(this.feedTapVideoId) === Number(video.id)) {
        window.clearTimeout(this.feedTapTimer)
        this.feedTapTimer = null
        this.feedTapVideoId = 0
        this.likeFromDoubleTap(video)
        return
      }
      window.clearTimeout(this.feedTapTimer)
      this.feedTapVideoId = Number(video.id)
      this.feedTapTimer = window.setTimeout(() => {
        this.feedTapTimer = null
        this.feedTapVideoId = 0
        this.togglePlayback(video)
      }, 260)
    },
    pulseVideoHeart(video) {
      video.like_burst_key = Number(video.like_burst_key || 0) + 1
      video.like_burst = false
      this.$nextTick(() => {
        video.like_burst = true
        window.setTimeout(() => { video.like_burst = false }, 620)
      })
    },
    likeFromDoubleTap(video) {
      this.pulseVideoHeart(video)
      if (!this.ensureLogin({ action: "like", videoId: video.id })) return
      if (video.user_state.liked || (video.action_loading && video.action_loading.like)) return
      this.updateVideoAction(video, "like", 1)
    },
    toggleMute() {
      this.muted = !this.muted
      const activeElement = this.activeVideo && (this.videoElement(this.activeVideo.id) || this.imagePostAudioElement(this.activeVideo.id))
      const centerElement = this.centerVideoVisible ? this.centerMediaElement() : null
      const elements = centerElement ? [centerElement] : [activeElement]
      elements.forEach(element => {
        if (!element) return
        element.muted = this.muted
        if (element.paused) element.play().catch(() => {})
      })
    },
    handleImageMusicError(video) {
      video.music_playback_error = true
      const centerAudio = this.centerVideoVisible && this.centerVideoDetail && Number(this.centerVideoDetail.id) === Number(video.id)
        ? this.$refs.centerImageAudioPlayer
        : null
      const element = centerAudio || this.imagePostAudioElement(video.id)
      if (element) element.pause()
    },
    handleImageMusicReady(video) {
      if (!video || video.music_playback_error || !this.activeVideo || Number(this.activeVideo.id) !== Number(video.id)) return
      this.playCurrent()
    },
    handleLoaded(video) {
      video.load_state = "ready"
      if (this.activeVideo && this.activeVideo.id === video.id) this.playCurrent()
    },
    handleCanPlay(video) {
      // canplay 与播放事件的先后在不同浏览器中不一致；可播放后立即停掉进度条缓冲提示。
      if (video.load_state === "loading") video.load_state = "ready"
      if (!video.user_paused && !video.playing && this.activeVideo && Number(this.activeVideo.id) === Number(video.id)) this.playCurrent()
    },
    handleVideoWaiting(video) {
      if (video.available && !video.user_paused) video.load_state = "loading"
    },
    handlePlay(video) {
      // 某些浏览器会先触发 play、后触发 loadeddata。已实际播放时必须立刻停掉缓冲提示。
      video.load_state = "ready"
      video.playing = true
      if (!video.view_started) { video.view_started = true; this.markRecommendationSeen(video); this.recordWatch(video, "start", 0, 0) }
    },
    handlePause(video) { video.playing = false },
    handleTimeUpdate(video, event) {
      const element = event.target
      if (!element.duration) return
      const progress = Math.min(100, Math.round((element.currentTime / element.duration) * 100))
      video.progress = progress
      const second = Math.floor(element.currentTime)
      if (second > 0 && second % 5 === 0 && second !== video.last_report_second) {
        video.last_report_second = second
        this.recordWatch(video, "progress", second, progress)
      }
      if (progress >= 90 && !video.completed) { video.completed = true; this.recordWatch(video, "complete", second, progress) }
    },
    handleVideoError(video) {
      video.load_state = "error"
      video.playing = false
      this.recordWatch(video, "error", 0, video.progress)
    },
    retryVideo(video) {
      video.load_state = "loading"
      if (video.live_source === "tiktok") {
        video.live_stream_ready = false
        const element = this.videoElement(video.id)
        if (element) this.prepareLiveStream(video, element).then(ready => { if (ready) this.playCurrent() })
        return
      }
      const separator = video.play_url.includes("?") ? "&" : "?"
      video.play_url = `${this.weakNetwork ? video.low_video_url : video.video_url}${separator}retry=${Date.now()}`
      this.$nextTick(() => { const element = this.videoElement(video.id); if (element) element.load() })
    },
    recordWatch(video, event, watchedSeconds, progress) {
      addWatch({ video_id: video.id, event, watched_seconds: watchedSeconds, progress, network_type: this.networkType() }).then(res => {
        // 播放量以服务端去重后的真实统计为准。
        video.stats.view = res.data.view_count
      }).catch(error => {
        if (error.responseData && error.responseData.code === 410) { video.available = false; video.placeholder = this.$t("appUi.contentDelisted") }
      })
    },
    detectNetwork() {
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection
      this.weakNetwork = Boolean(connection && (connection.saveData || ["slow-2g", "2g"].includes(connection.effectiveType)))
    },
    networkType() {
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection
      return connection ? connection.effectiveType || "unknown" : "unknown"
    },
    ensureLogin(action) {
      if (this.currentUser) return true
      this.showAuth("login", action)
      return false
    },
    async updateVideoAction(video, action, status) {
      if (!video.action_loading) this.$set(video, "action_loading", { like: false, favorite: false })
      if (video.action_loading[action]) return
      this.$set(video.action_loading, action, true)
      const field = action === "like" ? "liked" : "favorited"
      const counter = action === "like" ? "like" : "favorite"
      try {
        const res = await updateAction({ video_id: video.id, action, status })
        video.user_state[field] = status === 1
        video.stats[counter] = res.data.count
      } finally {
        this.$set(video.action_loading, action, false)
      }
    },
    toggleAction(video, action) {
      if (!this.ensureLogin({ action, videoId: video.id })) return
      const field = action === "like" ? "liked" : "favorited"
      const status = video.user_state[field] ? 0 : 1
      this.updateVideoAction(video, action, status)
    },
    toggleFollow(video) {
      if (!video || !video.author || video.author.is_self) return
      if (!this.ensureLogin({ action: "follow", videoId: video.id })) return
      if (video.follow_loading) return
      const status = video.author.followed ? 0 : 1
      video.follow_loading = true
      updateFollow({ author_id: video.author.id, status }).then(res => {
        this.syncAuthorFollow(video.author.id, Boolean(res.data.followed), Number(res.data.follower_count))
      }).finally(() => { video.follow_loading = false })
    },
    syncAuthorFollow(authorId, followed, followerCount) {
      this.videos.forEach(item => {
        if (item.author && Number(item.author.id) === Number(authorId)) {
          item.author.followed = followed
          if (Number.isFinite(followerCount)) item.author.follower_count = followerCount
        }
      })
      if (this.liveRoomVideo && Number(this.liveRoomVideo.author.id) === Number(authorId)) {
        this.liveRoomVideo.author.followed = followed
        if (Number.isFinite(followerCount)) this.liveRoomVideo.author.follower_count = followerCount
      }
      this.liveRoomQueue.forEach(item => {
        if (item.author && Number(item.author.id) === Number(authorId)) {
          item.author.followed = followed
          if (Number.isFinite(followerCount)) item.author.follower_count = followerCount
        }
      })
      if (this.authorDetail && Number(this.authorDetail.author.id) === Number(authorId)) {
        this.authorDetail.author.followed = followed
        if (Number.isFinite(followerCount)) this.authorDetail.author.follower_count = followerCount
      }
    },
    openComments(video, focusCommentId = 0) {
      if (!this.ensureLogin({ action: "comment", videoId: video.id })) return
      this.activeTarget = video
      this.replyingComment = null
      this.focusedCommentId = 0
      return getComments({ video_id: video.id }).then(res => {
        this.comments = res.data.list
        this.commentsEnabled = res.data.comments_enabled !== false
        this.commentsVisible = true
        this.$nextTick(() => this.focusComment(focusCommentId))
      })
    },
    focusComment(commentId) {
      const targetId = Number(commentId)
      if (!targetId) return
      const target = this.$refs.commentList && this.$refs.commentList.querySelector(`[data-comment-id="${targetId}"]`)
      if (!target) { this.$notice.info(this.$t("appUi.commentUnavailable")); return }
      this.focusedCommentId = targetId
      target.scrollIntoView({ behavior: "smooth", block: "center" })
      window.setTimeout(() => { if (this.focusedCommentId === targetId) this.focusedCommentId = 0 }, 2200)
    },
    startReply(comment) { this.replyingComment = comment; this.commentText = "" },
    cancelReply() { this.replyingComment = null },
    postComment() {
      if (!this.commentsEnabled || !this.commentText || !this.activeTarget) return
      addComment({ video_id: this.activeTarget.id, parent_id: this.replyingComment ? this.replyingComment.id : 0, content: this.commentText }).then(res => {
        this.comments.unshift(res.data.comment)
        // 始终采用服务端按真实评论记录计算后的数量，避免并发操作造成前端数字漂移。
        this.activeTarget.stats.comment = res.data.comment_count
        this.commentText = ""
        this.replyingComment = null
      })
    },
    removeComment(comment) {
      this.$notice.confirm(this.$t("appUi.deleteCommentConfirm"), this.$t("appUi.deleteComment"), { type: "warning" }).then(() => deleteComment({ comment_id: comment.id })).then(res => {
        const deletedIds = new Set((res.data.comment_ids || [res.data.comment_id]).map(Number))
        this.comments = this.comments.filter(item => !deletedIds.has(Number(item.id)))
        if (this.activeTarget) this.activeTarget.stats.comment = res.data.comment_count
      }).catch(() => {})
    },
    toggleCommentLike(comment) {
      const status = comment.liked ? 0 : 1
      updateCommentLike({ comment_id: comment.id, status }).then(res => { comment.liked = res.data.liked; comment.like_count = res.data.like_count })
    },
    blockCommentUser(comment) {
      this.$notice.confirm(this.$t("appUi.blockUserConfirm", { name: comment.user_name }), this.$t("appUi.confirmBlock"), { type: "warning" }).then(() => updateBlock({ target_type: "user", target_id: comment.user_id, status: 1 })).then(() => {
        this.comments = this.comments.filter(item => item.user_id !== comment.user_id)
        this.$notice.success(this.$t("appUi.userBlocked"))
      }).catch(() => {})
    },
    openShare(video) {
      if (!this.ensureLogin({ action: "share", videoId: video.id })) return
      this.activeTarget = video; this.shareVisible = true
    },
    async performShare(channel) {
      if (!this.activeTarget) return
      const video = this.activeTarget
      const shareUrl = this.buildVideoShareUrl(video.id)
      if (channel === "internal") {
        this.shareVisible = false
        this.openChatShare({ ...video, content_type: video.content_type === "live" ? "live" : "video" })
        return
      }
      if (channel === "system" && !navigator.share) { this.$notice.info(this.$t("appUi.systemShareUnsupported")); return }
      if (channel === "system") {
        try { await navigator.share({ title: video.title, text: video.description, url: shareUrl }) } catch (error) { return }
      }
      if (channel === "copy") {
        try { await navigator.clipboard.writeText(shareUrl) } catch (error) { this.$notice.warning(this.$t("appUi.copyFailed")) ; return }
      }
      addShare({ video_id: video.id, channel }).then(res => {
        video.stats.share = res.data.share_count; this.shareVisible = false
        this.$notice.success(this.$t(channel === "internal" ? "appUi.internalShared" : channel === "copy" ? "appUi.linkCopied" : "appUi.shareSuccess"))
      })
    },
    buildVideoShareUrl(videoId) {
      const url = new URL(window.location.href)
      url.searchParams.set("video", String(videoId))
      return url.toString()
    },
    async openSharedTargetFromUrl() {
      const params = new URL(window.location.href).searchParams
      const videoId = Number(params.get("video"))
      const authorId = Number(params.get("author"))
      try {
        // 直播也是短视频事实，优先按 video 深链进入真实直播间。
        if (Number.isInteger(videoId) && videoId > 0) {
          await this.openDirectVideo(videoId)
          return
        }
        if (Number.isInteger(authorId) && authorId > 0) await this.openAuthor(authorId)
      } catch (error) {
        // 接口层会统一展示加载失败提示，避免无效分享链接中断首页初始化。
      }
    },
    openMore(video) { this.activeTarget = video; this.moreVisible = true },
    markNotInterested() {
      if (!this.activeTarget || !this.ensureLogin({ action: "not_interested", videoId: this.activeTarget.id })) return
      updateAction({ video_id: this.activeTarget.id, action: "not_interested", status: 1 }).then(() => {
        const removeIndex = this.videos.findIndex(item => item.id === this.activeTarget.id)
        this.videos.splice(removeIndex, 1)
        this.currentIndex = Math.min(this.currentIndex, Math.max(0, this.videos.length - 1))
        this.moreVisible = false
        this.$nextTick(() => this.playCurrent())
      })
    },
    openReport(targetType, targetId) {
      if (!targetId || !this.ensureLogin({ action: "report", targetType, targetId })) return
      this.reportTarget = { targetType, targetId }
      this.reportForm = { reason: "spam", detail: "", evidence: "" }
      this.reportReasonVisible = true
    },
    submitReport() {
      if (!this.reportTarget) return
      addReport({ target_type: this.reportTarget.targetType, target_id: this.reportTarget.targetId, ...this.reportForm }).then(res => {
        this.reportReasonVisible = false; this.moreVisible = false; this.$notice.success(this.$t("common.success"))
      })
    },
    openAuthor(authorId) {
      const requestId = ++this.authorRequestId
      this.authorVisible = true
      this.authorDetail = null
      this.authorShop = null
      this.authorWorkTab = "video"
      this.authorLoading = true
      this.authorWorksLoading = true
      this.authorShopLoading = false
      return getAuthor({ id: authorId, type: "video", page: 1, limit: 9 }).then(authorRes => {
        if (requestId !== this.authorRequestId) return
        this.authorDetail = authorRes.data
      }).finally(() => {
        if (requestId === this.authorRequestId) {
          this.authorLoading = false
          this.authorWorksLoading = false
        }
      })
    },
    // 作者主页只在作品 Tab 请求分页；礼物支持和专属内容随作者橱窗一次加载。
    switchAuthorWorkTab(type) {
      if (!this.authorDetail || this.authorWorkTab === type) return
      this.authorWorkTab = type
      if (type !== "video") {
        this.loadAuthorShop(this.authorDetail.author.id)
        return
      }
      this.authorWorksLoading = true
      getAuthor({ id: this.authorDetail.author.id, type: "video", page: 1, limit: 9 }).then(res => {
        this.authorDetail.videos = res.data.videos; this.authorDetail.pagination = res.data.pagination; this.authorDetail.work_counts = res.data.work_counts
        this.authorDetail.live_room = res.data.live_room
      }).finally(() => { this.authorWorksLoading = false })
    },
    loadAuthorShop(authorId) {
      const id = Number(authorId)
      if (!id || this.authorShopLoading) return Promise.resolve()
      const cached = this.authorShopCache[id]
      if (cached && Date.now() - cached.loadedAt < 60000) {
        this.authorShop = cached.data
        return Promise.resolve(cached.data)
      }
      this.authorShopLoading = true
      return getCreatorShop({ author_id: id }).then(res => {
        const entry = { data: res.data, loadedAt: Date.now() }
        this.$set(this.authorShopCache, id, entry)
        if (this.authorDetail && Number(this.authorDetail.author.id) === id) this.authorShop = entry.data
        return entry.data
      }).finally(() => { this.authorShopLoading = false })
    },
    loadMoreAuthorWorks() {
      if (!this.authorDetail || this.authorWorksLoading || !this.authorDetail.pagination.has_more) return
      this.authorWorksLoading = true
      const page = this.authorDetail.pagination.page + 1
      getAuthor({ id: this.authorDetail.author.id, type: "video", page, limit: 9 }).then(res => {
        this.authorDetail.videos.push(...res.data.videos); this.authorDetail.pagination = res.data.pagination
      }).finally(() => { this.authorWorksLoading = false })
    },
    // 作者开播时，头像作为唯一直播入口；需要登录的直播会在登录后恢复打开。
    openAuthorLive() {
      const liveRoom = this.authorDetail && this.authorDetail.live_room
      if (!liveRoom) return
      if (!liveRoom.available && !this.ensureLogin({ action: "author_work", videoId: liveRoom.id })) return
      return this.openDirectVideo(liveRoom.id)
    },
    // 作者主页的作品覆盖当前页面播放，关闭后继续停留在原作者主页。
    openAuthorWork(item) {
      if (!item.available && !this.ensureLogin({ action: "author_work", videoId: item.id })) return
      return this.openDirectVideo(item.id)
    },
    toggleAuthorDetailFollow() {
      if (!this.authorDetail || this.authorDetail.author.is_self || !this.ensureLogin({ action: "author_follow", authorId: this.authorDetail.author.id })) return
      const status = this.authorDetail.author.followed ? 0 : 1
      updateFollow({ author_id: this.authorDetail.author.id, status }).then(res => {
        this.syncAuthorFollow(this.authorDetail.author.id, Boolean(res.data.followed), Number(res.data.follower_count))
      })
    },
    async joinOrOpenAuthorFanGroup() {
      const group = this.authorDetail && this.authorDetail.fan_group
      if (!group || !this.ensureLogin({ action: "author_fan_group", authorId: this.authorDetail.author.id })) return
      this.fanGroupJoining = true
      try {
        if (!group.joined) {
          const res = await joinFanGroup({ group_id: group.id })
          this.authorDetail.fan_group = { ...group, ...res.data.group, joined: true }
          this.$notice.success(this.$t('fanGroup.joined'))
        }
        this.authorVisible = false
        this.handleMessages()
        await this.$nextTick()
        if (this.$refs.notificationCenter && this.$refs.notificationCenter.openFanGroup) await this.$refs.notificationCenter.openFanGroup(group.id)
      } finally { this.fanGroupJoining = false }
    },
    toggleAuthorBlock() {
      if (!this.authorDetail || !this.ensureLogin({ action: "author_block", authorId: this.authorDetail.author.id })) return
      const status = this.authorDetail.author.blocked ? 0 : 1
      const action = () => updateBlock({ target_type: "author", target_id: this.authorDetail.author.id, status }).then(() => {
        this.authorDetail.author.blocked = status === 1
        if (status === 1) {
          this.authorDetail.videos = []
          this.authorDetail.live_room = null
        }
        this.$notice.success(this.$t(status === 1 ? "appUi.authorBlocked" : "appUi.authorUnblocked"))
        this.loadFeed()
      })
      if (status === 1) this.$notice.confirm(this.$t("appUi.blockAuthorConfirm"), this.$t("appUi.confirmBlock"), { type: "warning" }).then(action).catch(() => {})
      else action()
    },
    blockActiveAuthor() {
      if (!this.activeTarget || !this.ensureLogin({ action: "block_author", videoId: this.activeTarget.id })) return
      this.$notice.confirm(this.$t("appUi.blockNamedAuthorConfirm", { name: this.activeTarget.author.name }), this.$t("appUi.confirmBlock"), { type: "warning" }).then(() => updateBlock({ target_type: "author", target_id: this.activeTarget.author.id, status: 1 })).then(() => {
        this.moreVisible = false; this.$notice.success(this.$t("appUi.authorBlocked")); this.loadFeed()
      }).catch(() => {})
    },
    openUserPublish() {
      if (!this.ensureLogin({ action: "user_publish" })) return
      this.pauseAll()
      if (this.$refs.userPublish) this.$refs.userPublish.open()
    },
    closeUserPublish() {
      if (this.publishVisible && this.$refs.userPublish) this.$refs.userPublish.close()
    },
    handleUserPublished() {
      if (this.$refs.userCenter && this.$refs.userCenter.refreshWorks) this.$refs.userCenter.refreshWorks()
    },
    openStudio() {
      if (!this.ensureLogin({ action: "studio" })) return
      this.handleProfile()
      this.$nextTick(() => {
        if (this.$refs.userCenter && this.$refs.userCenter.openWorkspace) this.$refs.userCenter.openWorkspace()
      })
    },
    openStudioRoleApplication(type) {
      if (type !== "streamer") {
        this.$notice.info(this.$t("studioUi.creatorApplicationClosed"))
        return
      }
      this.studioVisible = false
      this.pauseAll()
      this.settingsInitialPanel = "streamer-access"
      this.settingsGuildRequestId = 0
      this.settingsSupportTicketId = 0
      this.settingsVisible = true
    },
    openStudioLive(videoId) {
      // 保留创作中心作为直播间底层页面，关闭直播间后可继续管理本场直播。
      return this.openDirectVideo(videoId)
    },
    openTip(project) {
      if (!this.ensureLogin({ action: "tip", authorId: this.authorDetail.author.id, projectId: project.id })) return
      this.selectedTip = project; this.tipVisible = true
    },
    openGift(gift) {
      if (!this.ensureLogin({ action: "gift", authorId: this.authorDetail.author.id, giftId: gift.id })) return
      const randomPart = typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID().replace(/-/g, "")
        : `${Date.now()}${Math.random().toString(16).slice(2)}`
      this.selectedGift = gift
      this.giftQuantity = 1
      this.giftRequestNo = `GIFT${randomPart}`.slice(0, 64)
      this.giftVisible = true
    },
    confirmGift() {
      if (!this.selectedGift || this.coinPayLoading) return
      const amount = this.selectedGift.coin_price * this.giftQuantity
      this.$notice.confirm(this.$t("giftUi.confirmText", { amount: this.$n(amount), title: this.selectedGift.name, count: this.$n(this.giftQuantity) }), this.$t("appUi.coinPaymentConfirm"), { type: "warning", confirmButtonText: this.$t("appUi.confirmPayment") }).then(() => {
        this.coinPayLoading = true
        return addGift({
          author_id: this.authorDetail.author.id,
          gift_id: this.selectedGift.id,
          quantity: this.giftQuantity,
          client_request_no: this.giftRequestNo,
          confirm: 1
        })
      }).then(res => {
        if (this.authorShop && this.authorShop.wallet) this.authorShop.wallet.balance = res.data.balance
        this.giftVisible = false
        this.refreshNotificationInbox()
        this.$notice.success(this.$t("giftUi.sent", { title: res.data.gift_name, count: this.$n(res.data.quantity) }))
      }).catch(() => {}).finally(() => { this.coinPayLoading = false })
    },
    confirmTip() {
      if (!this.selectedTip || this.coinPayLoading) return
      this.$notice.confirm(this.$t("appUi.tipConfirm", { amount: this.$n(this.selectedTip.coin_price), title: this.selectedTip.title }), this.$t("appUi.coinPaymentConfirm"), { type: "warning", confirmButtonText: this.$t("appUi.confirmPayment") }).then(() => {
        this.coinPayLoading = true
        return addTip({ project_id: this.selectedTip.id, confirm: 1 })
      }).then(res => {
        if (this.authorShop && this.authorShop.wallet) this.authorShop.wallet.balance = res.data.balance
        this.tipVisible = false; this.refreshNotificationInbox(); this.$notice.success(this.$t("common.success"))
      }).catch(() => {}).finally(() => { this.coinPayLoading = false })
    },
    openPaidContent(content) {
      const contentId = Number(content.id)
      return getPaidContent({ content_id: contentId }).then(res => { this.paidDetail = res.data.content; this.paidVisible = true })
    },
    buyPaidContent() {
      if (!this.paidDetail || !this.ensureLogin({ action: "purchase", authorId: this.paidDetail.author_id, contentId: this.paidDetail.id })) return
      if (this.coinPayLoading) return
      this.$notice.confirm(this.$t("appUi.purchaseConfirm", { amount: this.$n(this.paidDetail.coin_price), title: this.paidDetail.title }), this.$t("appUi.coinPaymentConfirm"), { type: "warning", confirmButtonText: this.$t("appUi.confirmPurchase") }).then(() => {
        this.coinPayLoading = true
        return addPurchase({ content_id: this.paidDetail.id, confirm: 1 })
      }).then(res => {
        this.paidDetail = res.data.content
        if (this.authorShop && this.authorShop.wallet) this.authorShop.wallet.balance = res.data.balance
        if (this.authorShop) {
          const content = this.authorShop.paid_contents.find(item => item.id === this.paidDetail.id)
          if (content) Object.assign(content, this.paidDetail)
        }
        this.refreshNotificationInbox()
        this.$notice.success(this.$t("common.success"))
      }).catch(error => {
        const data = error.responseData && error.responseData.data
        if (data && data.already_purchased && data.content) this.paidDetail = data.content
      }).finally(() => { this.coinPayLoading = false })
    },
    resetWalletLists() {
      this.walletTransactions = []
      this.walletPurchases = []
      this.walletPagination = {
        transactions: { page: 0, limit: 20, total: 0, has_more: true, loaded: false, loading: false },
        purchases: { page: 0, limit: 20, total: 0, has_more: true, loaded: false, loading: false }
      }
    },
    async loadWalletList(type, append = false) {
      const state = this.walletPagination[type]
      if (!state || state.loading || (append && !state.has_more)) return
      state.loading = true
      const page = append ? state.page + 1 : 1
      try {
        const response = type === "purchases"
          ? await getPurchases({ page, limit: state.limit })
          : await getCoinTransactions({ page, limit: state.limit })
        const rows = response.data.list || []
        const target = type === "purchases" ? "walletPurchases" : "walletTransactions"
        if (append) {
          const existingIds = new Set(this[target].map(item => Number(item.id)))
          this[target] = this[target].concat(rows.filter(item => !existingIds.has(Number(item.id))))
        } else {
          this[target] = rows
        }
        Object.assign(state, {
          page: Number(response.data.page || page),
          limit: Number(response.data.limit || state.limit),
          total: Number(response.data.count || 0),
          has_more: Boolean(response.data.has_more),
          loaded: true
        })
      } finally {
        state.loading = false
      }
    },
    loadMoreWalletList(type) {
      return this.loadWalletList(type, true)
    },
    handleWalletTabClick(tab) {
      const type = tab && tab.name
      if (this.walletPagination[type] && !this.walletPagination[type].loaded) this.loadWalletList(type)
    },
    openWalletDetails() {
      if (!this.ensureLogin({ action: "wallet" })) return
      this.walletTab = "transactions"
      this.resetWalletLists()
      Promise.all([getWallet(), this.loadWalletList("transactions")]).then(([walletRes]) => {
        this.walletData = walletRes.data; this.walletVisible = true
      })
    },
    handleProfile() {
      if (!this.currentUser) { this.showAuth("login", { action: "profile" }); return }
      this.closeUserPublish()
      this.pauseAll(); this.closeCenterSubpages(); this.studioVisible = false; this.messageVisible = false; this.discoverVisible = false; this.profileVisited = true; this.profileVisible = true; this.activeRootTab = "profile"
    },
    openSettings() {
      if (!this.currentUser) { this.showAuth("login", { action: "profile" }); return }
      this.pauseAll()
      this.settingsInitialPanel = ""
      this.settingsGuildRequestId = 0
      this.settingsSupportTicketId = 0
      this.settingsVisible = true
    },
    closeSettings() {
      this.settingsVisible = false
      this.clearGuildSettingsTarget()
      this.clearSupportSettingsTarget()
    },
    openPWAInstall() {
      if (this.$refs.pwaController) this.$refs.pwaController.openInstallGuide()
    },
    openProfileSettings() {
      this.settingsVisible = false
      this.handleProfile()
      this.$nextTick(() => {
        if (this.$refs.userCenter && this.$refs.userCenter.openProfileEditor) this.$refs.userCenter.openProfileEditor()
      })
    },
    openLegalDocument(type) {
      this.legalType = ["terms", "privacy", "community"].includes(type) ? type : "terms"
      this.legalVisible = true
    },
    closeLegalDocument() {
      this.legalVisible = false
    },
    openNotificationSettings() {
      this.settingsVisible = false
      this.handleMessages()
      this.$nextTick(() => {
        if (this.$refs.notificationCenter && this.$refs.notificationCenter.openSettings) this.$refs.notificationCenter.openSettings()
      })
    },
    handleMessages() {
      if (!this.currentUser) { this.showAuth("login", { action: "message_center" }); return }
      const wasVisible = this.messageVisible
      this.closeUserPublish()
      this.pauseAll(); this.closeCenterSubpages(); this.studioVisible = false; this.profileVisible = false; this.discoverVisible = false; this.messageVisited = true; this.messageVisible = true; this.activeRootTab = "message"
      this.$nextTick(() => {
        // 仅从其他页面回到朋友页时关闭详情；已缓存的会话列表优先立即恢复。
        if (!wasVisible && this.$refs.notificationCenter && this.$refs.notificationCenter.resetInbox) this.$refs.notificationCenter.resetInbox()
        if (this.$refs.notificationCenter && this.$refs.notificationCenter.activate) this.$refs.notificationCenter.activate()
      })
    },
    openDirectChat(target) {
      if (!this.currentUser) { this.showAuth("login", { action: "message_center" }); return }
      this.authorVisible = false
      this.handleMessages()
      this.$nextTick(() => {
        if (this.$refs.notificationCenter && this.$refs.notificationCenter.openChat) {
          this.$refs.notificationCenter.openChat(target).catch(() => {})
        }
      })
    },
    openFanGroupFromStudio(groupId) {
      if (!groupId || !this.currentUser) return
      this.handleMessages()
      this.$nextTick(() => {
        if (this.$refs.notificationCenter && this.$refs.notificationCenter.openFanGroup) {
          this.$refs.notificationCenter.openFanGroup(groupId).catch(() => {})
        }
      })
    },
    openChatShare(content) {
      if (!content || !content.id) return
      if (!this.currentUser) { this.showAuth("login", { action: "message_center" }); return }
      this.chatShareTarget = {
        id: Number(content.id),
        content_type: content.content_type || "video",
        title: content.title || "",
        description: content.description || "",
        cover_url: content.cover_url || content.banner_url || ""
      }
      this.chatShareVisible = true
    },
    closeChatShare() { this.chatShareVisible = false; this.chatShareTarget = null },
    async handleChatShareSent(payload) {
      const shared = payload && payload.content
      this.closeChatShare()
      if (shared && ["video", "live"].includes(shared.content_type)) {
        try {
          const res = await addShare({ video_id: Number(shared.id), channel: "internal" })
          const video = this.videos.find(item => Number(item.id) === Number(shared.id))
          if (video && video.stats) video.stats.share = res.data.share_count
        } catch (error) { /* 消息已成功发送时，不因计数接口失败误报分享失败。 */ }
      }
      this.$notice.success(this.$t("directMessage.contentSent"))
    },
    openChatPeerProfile(peer) {
      if (!peer) return
      if (Number(peer.author_id) > 0) { this.openAuthor(Number(peer.author_id)); return }
      this.messageVisible = false
      this.pauseAll(); this.closeCenterSubpages(); this.discoverVisible = false; this.studioVisible = false
      this.profileVisited = true; this.profileVisible = true; this.activeRootTab = "profile"
      this.$nextTick(() => {
        if (this.$refs.userCenter && this.$refs.userCenter.openMemberProfile) this.$refs.userCenter.openMemberProfile(Number(peer.user_id)).catch(() => {})
      })
    },
    openChatSharedContent(item) {
      if (!item || !item.id) return
      if (["video", "live"].includes(item.content_type)) return this.openDirectVideo(Number(item.id))
      return this.openDiscoverContent(item)
    },
    async startChatMusic(item, done) {
      try {
        if (!item || Number(item.id) <= 0) return
        this.discoverVisited = true
        await this.$nextTick()
        const page = this.$refs.discoverPage
        if (page && page.playMusicFromCard) await page.playMusicFromCard(Number(item.id))
      } finally {
        if (typeof done === "function") done()
      }
    },
    selectNotificationVideo(videoId) { return this.openDirectVideo(videoId) },
    selectNotificationComment(target) {
      return this.openDirectVideo(target.videoId).then(video => {
        if (!video) return
        return this.$nextTick().then(() => {
          const player = this.$refs.centerVideoPlayer
          if (player) player.pause()
          return this.openComments(video, target.commentId)
        })
      })
    },
    openNotificationTransactions() {
      // 钱包作为消息页上方的业务覆盖层打开，关闭后仍回到消息列表。
      this.walletTab = "transactions"
      this.openWalletDetails()
    },
    closeCenterSubpages() {
      if (this.$refs.userCenter && this.$refs.userCenter.closeSubpages) this.$refs.userCenter.closeSubpages()
    },
    openNotificationPaidContent(contentId) { this.openPaidContent({ id: contentId }) },
    openNotificationGuild(requestId) {
      this.pauseAll()
      this.settingsInitialPanel = "guild"
      this.settingsGuildRequestId = Number(requestId || 0)
      this.settingsVisible = true
    },
    clearGuildSettingsTarget() {
      if (this.settingsInitialPanel === "guild") this.settingsInitialPanel = ""
      this.settingsGuildRequestId = 0
    },
    openNotificationSupport(ticketId) {
      this.pauseAll()
      this.settingsInitialPanel = "support"
      this.settingsSupportTicketId = Number(ticketId || 0)
      this.settingsVisible = true
    },
    clearSupportSettingsTarget() {
      if (this.settingsInitialPanel === "support") this.settingsInitialPanel = ""
      this.settingsSupportTicketId = 0
    },
    handleGuildMembershipChanged() {
      this.loadProfile()
      this.refreshNotificationInbox()
    },
    openNotificationProfile(tab = "home") {
      this.messageVisible = false; this.discoverVisible = false; this.profileVisited = true; this.profileVisible = true; this.activeRootTab = "profile"
      this.$nextTick(() => { if (this.$refs.userCenter && tab !== "home") this.$refs.userCenter.switchTab(tab) })
    },
    syncCenterProfile(profile) { this.currentUser = { ...this.currentUser, ...profile } },
    syncSecurityEmail(profile) { this.currentUser = { ...this.currentUser, ...profile } },
    // 消息、用户中心和作者主页共用直接观看层，不改变当前一级页面。
    openDirectVideo(videoId) {
      const previousCenterMedia = this.centerMediaElement()
      if (previousCenterMedia) previousCenterMedia.pause()
      this.pauseAll()
      return getVideoDetail({ id: Number(videoId) }).then(res => {
        if (!res.data.available || !res.data.video) { this.$notice.warning(this.$t("feed.unavailable")); return }
        const video = this.decorateVideo(res.data.video)
        if (video.content_type === "live" && video.live_source === "tiktok") {
          this.openLiveRoom(video)
          return video
        }
        this.centerVideoDetail = video
        this.centerVideoVisible = true
        if (video.content_type === "image_text" && video.music_audio_url) {
          this.$nextTick(() => {
            const audio = this.$refs.centerImageAudioPlayer
            if (!audio) return
            this.pauseAmbientMusic()
            audio.muted = this.muted
            audio.play().catch(() => {})
          })
        }
        if (video.live_source === "tiktok") {
          this.$nextTick(() => {
            const player = this.$refs.centerVideoPlayer
            if (player) this.prepareLiveStream(video, player).then(ready => {
              if (ready) player.play().catch(() => {})
            })
          })
        }
        return video
      })
    },
    selectCenterVideo(videoId) { return this.openDirectVideo(videoId) },
    closeCenterVideo() {
      const player = this.centerMediaElement()
      if (player) player.pause()
      this.centerVideoDetail = null
    },
    openCenterVideoComments() {
      const player = this.centerMediaElement()
      if (player) player.pause()
      this.openComments(this.centerVideoDetail)
    },
    openCenterVideoAuthor() {
      if (!this.centerVideoDetail) return
      const authorId = Number(this.centerVideoDetail.author.id)
      const player = this.centerMediaElement()
      if (player) player.pause()
      this.centerVideoVisible = false
      this.openAuthor(authorId)
    },
    openCenterAuthor(authorId) { this.openAuthor(Number(authorId)) },
    openCenterDiscoverContent(item) {
      if (!item || !item.id) return
      this.openDiscoverContent(item)
    },
    openFeedDiscovery(item) {
      if (!item || !item.content_id) return
      this.markDiscoveryFeedSeen(item)
      addDiscoveryEvent({
        event_name: "click_home_discovery",
        content_type: item.content_type,
        content_id: item.content_id,
        page_name: "home",
        channel_name: this.activeChannel,
        module_name: "recommend_feed",
        content_position: this.currentIndex + 1,
        language: navigator.language || "zh-CN"
      }).catch(() => {})
      this.openDiscoverContent({ id: item.content_id, content_type: item.content_type })
    },
    openDiscoverContent(item) {
      if (!item || !item.id) return
      this.profileVisible = false
      this.messageVisible = false
      this.discoverVisited = true
      this.discoverVisible = true
      this.activeRootTab = "discover"
      this.$nextTick(async () => {
        const page = this.$refs.discoverPage
        if (!page) return
        await page.initialize()
        if (item.content_type === "drama") page.openDrama(Number(item.id))
        else if (item.content_type === "music") page.openMusic(item)
        else if (item.content_type === "game") page.openGame(item)
        else page.openNovel(Number(item.id))
      })
    },
    discoveryFeedIcon(type) {
      return { drama: "el-icon-video-play", novel: "el-icon-reading", music: "el-icon-headset", game: "el-icon-magic-stick" }[type] || "el-icon-discover"
    },
    discoveryFeedMeta(item) {
      if (item.content_type === "drama") return this.$t("feedDiscovery.episodeMeta", { current: this.$n(item.updated_episodes), total: this.$n(item.total_episodes) })
      if (item.content_type === "novel") return item.author || item.category_name
      if (item.content_type === "music") return item.artist || item.category_name
      return item.category_name || this.$t("feedDiscovery.gameMeta")
    },
    discoveryFeedExclusions() {
      try {
        const history = JSON.parse(window.localStorage.getItem("starlive_home_discovery_seen") || "[]")
        return Array.isArray(history) ? history.slice(-12).join(",") : ""
      } catch (error) {
        return ""
      }
    },
    markDiscoveryFeedSeen(item) {
      if (!item || item.feed_kind !== "discovery" || !item.content_id) return
      const key = `${item.content_type}:${Number(item.content_id)}`
      let history = []
      try {
        const saved = JSON.parse(window.localStorage.getItem("starlive_home_discovery_seen") || "[]")
        history = Array.isArray(saved) ? saved : []
      } catch (error) { history = [] }
      history = history.filter(value => value !== key).concat(key).slice(-12)
      window.localStorage.setItem("starlive_home_discovery_seen", JSON.stringify(history))
      if (item.impression_tracked) return
      item.impression_tracked = true
      addDiscoveryEvent({
        event_name: "expose_home_discovery",
        content_type: item.content_type,
        content_id: item.content_id,
        page_name: "home",
        channel_name: this.activeChannel,
        module_name: "recommend_feed",
        content_position: this.currentIndex + 1,
        language: navigator.language || "zh-CN"
      }).catch(() => {})
    },
    hideDiscoveryFeedImage(event) { event.target.style.opacity = "0" },
    openCenterPaidContent(contentId) { this.openPaidContent({ id: contentId }) },
    async logoutCurrentUser() {
      try { await logoutAccount() } catch (error) { /* 本地仍需清理过期或已失效的会话。 */ }
      clearAuthToken(); this.currentUser = null; this.settingsVisible = false; this.legalVisible = false; this.studioVisible = false; this.profileVisible = false; this.profileVisited = false; this.messageVisible = false; this.messageVisited = false; this.discoverVisible = false; this.notificationUnread = 0; this.activeRootTab = "home"; this.loadFeed(); this.$notice.success(this.$t("appUi.loggedOut"))
    },
    showAuth(mode = "login", pendingAction = null) {
      this.authMode = mode; this.pendingAction = pendingAction; this.authVisible = true
      if (pendingAction) sessionStorage.setItem("starlive_resume", JSON.stringify({ index: this.currentIndex, action: pendingAction }))
    },
    switchAuthMode(mode) {
      this.authMode = mode; this.authForm.code = ""; this.authForm.password = ""; this.authForm.passwordConfirmation = ""
      if (mode === "login") this.loginMethod = "password"
      this.stopCountdown(); if (this.$refs.authForm) this.$refs.authForm.clearValidate()
    },
    switchLoginMethod(method) {
      this.loginMethod = method; this.authForm.code = ""; this.authForm.password = ""
      this.stopCountdown(); if (this.$refs.authForm) this.$refs.authForm.clearValidate()
    },
    sendCode() {
      this.$refs.authForm.validateField("email", error => {
        if (error) return
        this.codeSending = true
        const scene = this.authMode === "reset" ? "reset_password" : this.authMode
        const request = this.authMode === "restore" ? privacyRecoveryCodeSend({ email: this.authForm.email }) : sendEmailCode({ email: this.authForm.email, scene })
        request.then(res => { this.$notice.success(this.$t("common.success")); this.startCountdown(res.data.countdown) }).catch(error => {
          const action = error.responseData && error.responseData.data && error.responseData.data.action
          if (action === "login" || action === "register") this.switchAuthMode(action)
        }).finally(() => { this.codeSending = false })
      })
    },
    submitAuth() {
      this.$refs.authForm.validate(valid => {
        if (!valid) return
        if (this.needsCode && !/^\d{6}$/.test(this.authForm.code)) { this.$notice.warning(this.$t("appUi.codeInvalid")); return }
        const legacyLoginPassword = this.authMode === "login" && this.loginMethod === "password" && this.authForm.password.length >= 1 && this.authForm.password.length <= 72
        if (this.needsPassword && !legacyLoginPassword && (!/^(?=.*[A-Za-z])(?=.*\d).{8,72}$/.test(this.authForm.password))) { this.$notice.warning(this.$t("appUi.passwordInvalid")); return }
        if (this.needsPasswordConfirmation && this.authForm.password !== this.authForm.passwordConfirmation) { this.$notice.warning(this.$t("appUi.passwordMismatch")); return }
        if (this.authMode === "register" && (!this.authForm.agreement || !this.authForm.privacy)) { this.$notice.warning(this.$t("appUi.agreementRequired")); return }
        this.authLoading = true
        const data = { email: this.authForm.email, remember: this.authForm.remember ? 1 : 0 }
        let request
        if (this.authMode === "restore") {
          request = privacyAccountDeleteCancel({ email: this.authForm.email, code: this.authForm.code })
        } else if (this.authMode === "login" && this.loginMethod === "password") {
          data.account = this.authForm.email; delete data.email; data.password = this.authForm.password; request = passwordLogin(data)
        } else if (this.authMode === "login") {
          data.code = this.authForm.code; request = loginAccount(data)
        } else if (this.authMode === "register") {
          Object.assign(data, { code: this.authForm.code, password: this.authForm.password, password_confirmation: this.authForm.passwordConfirmation, agreement: 1, privacy: 1 }); request = registerAccount(data)
        } else {
          Object.assign(data, { code: this.authForm.code, password: this.authForm.password, password_confirmation: this.authForm.passwordConfirmation }); request = resetPassword(data)
        }
        request.then(res => {
          if (this.authMode === "restore") { this.$notice.success(this.$t("privacyManagement.recoverySuccess")); this.switchAuthMode("login"); return }
          setAuthToken(res.data.token, this.authForm.remember); this.currentUser = res.data.user; this.authVisible = false; this.loadNotificationUnread(); this.$notice.success(this.$t("common.success")); this.restorePendingAction()
        }).catch(error => {
          const action = error.responseData && error.responseData.data && error.responseData.data.action
          const deletionPending = error.responseData && error.responseData.data && error.responseData.data.deletion_pending
          if (deletionPending) this.switchAuthMode("restore")
          if (action === "login" || action === "register") this.switchAuthMode(action)
          if (action === "reset_password") this.switchAuthMode("reset")
        }).finally(() => { this.authLoading = false })
      })
    },
    async handlePrivacyDataErased() {
      await this.loadProfile()
      this.loadFeed()
    },
    handleAccountDeletionRequested() {
      clearAuthToken(); this.currentUser = null; this.settingsVisible = false; this.profileVisible = false; this.profileVisited = false; this.messageVisible = false; this.messageVisited = false; this.studioVisible = false; this.activeRootTab = "home"; this.showAuth("restore")
    },
    restorePendingAction() {
      const text = sessionStorage.getItem("starlive_resume") || sessionStorage.getItem("liveframe_resume"); const resume = text ? JSON.parse(text) : null; sessionStorage.removeItem("starlive_resume"); sessionStorage.removeItem("liveframe_resume")
      if (!resume) { this.loadFeed(); return }
      this.pendingAction = null
      this.loadFeed().then(() => {
        this.$nextTick(() => { if (this.$refs.feed) this.$refs.feed.scrollTo({ top: (resume.index || 0) * this.$refs.feed.clientHeight }); this.executePending(resume.action) })
      })
    },
    executePending(action) {
      if (!action) return
      if (action.action === "channel") { const channel = this.channels.find(item => item.code === action.code); if (channel) this.switchChannel(channel); return }
      if (action.action === "author_follow" && this.authorDetail) { this.toggleAuthorDetailFollow(); return }
      if (action.action === "author_fan_group" && this.authorDetail) { this.joinOrOpenAuthorFanGroup(); return }
      if (action.action === "author_block" && this.authorDetail) { this.toggleAuthorBlock(); return }
      if (action.action === "studio" || action.action === "creator_application") { this.openStudio(); return }
      if (action.action === "user_publish") { this.openUserPublish(); return }
      if (action.action === "profile") { this.handleProfile(); return }
      if (action.action === "message_center") { this.handleMessages(); return }
      if (action.action === "wallet") { this.openWalletDetails(); return }
      if (action.action === "gift") { this.openAuthor(action.authorId).then(() => { const gift = this.authorShop.gifts.find(item => item.id === action.giftId); if (gift) this.openGift(gift) }); return }
      if (action.action === "tip") { this.openAuthor(action.authorId).then(() => { const project = this.authorShop.tip_projects.find(item => item.id === action.projectId); if (project) this.openTip(project) }); return }
      if (action.action === "purchase") { this.openAuthor(action.authorId).then(() => this.openPaidContent({ id: action.contentId })); return }
      if (action.action === "author_work") { this.openAuthorWork({ id: action.videoId, available: true }); return }
      if (action.action === "report" && !action.videoId) { this.openReport(action.targetType, action.targetId); return }
      if (action.action === "search_follow" && this.$refs.searchPage) { this.$refs.searchPage.followUserById(action.userId); return }
      const video = this.videos.find(item => item.id === action.videoId)
      if (!video) return
      if (["like", "favorite"].includes(action.action)) this.toggleAction(video, action.action)
      else if (action.action === "follow") this.toggleFollow(video)
      else if (action.action === "comment") this.openComments(video)
      else if (action.action === "share") this.openShare(video)
      else if (action.action === "not_interested") { this.activeTarget = video; this.markNotInterested() }
      else if (action.action === "block_author") { this.activeTarget = video; this.blockActiveAuthor() }
      else if (action.action === "report") this.openReport(action.targetType || "video", action.targetId || video.id)
    },
    async loadProfile() {
      if (!getAuthToken()) return
      try {
        const res = await getProfile(); this.currentUser = res.data.user
        await Promise.all([this.loadNotificationUnread(), this.loadDynamicIslandNotices()])
      } catch (error) {}
    },
    async loadNotificationUnread() {
      if (!this.currentUser) { this.notificationUnread = 0; return }
      try { const res = await getNotificationUnread(); this.notificationUnread = friendUnreadTotal(res.data.unread) } catch (error) {}
    },
    // 业务操作生成消息后统一刷新未读角标；消息页已创建时同时刷新当前列表。
    refreshNotificationInbox() {
      this.loadNotificationUnread()
      this.loadDynamicIslandNotices()
      if (this.$refs.notificationCenter) this.$refs.notificationCenter.loadMessages(true)
    },
    async loadDynamicIslandNotices() {
      if (!this.currentUser) { this.resetDynamicIsland(); return }
      const userId = Number(this.currentUser.id || 0)
      if (this.islandUserId !== userId) this.resetDynamicIsland(userId)
      try {
        const res = await getNotificationPopups({ after_live_id: this.islandLiveCursor, after_chat_id: this.islandChatCursor })
        const cursor = res.data.cursor || {}
        this.islandLiveCursor = Math.max(this.islandLiveCursor, Number(cursor.live_id || 0))
        this.islandChatCursor = Math.max(this.islandChatCursor, Number(cursor.chat_id || 0))
        const existing = new Set([...this.islandSeenKeys, ...[this.islandCurrent, ...this.islandQueue].filter(Boolean).map(item => item.key)])
        ;(res.data.list || []).forEach(item => {
          if (existing.has(item.key)) return
          existing.add(item.key)
          this.islandSeenKeys.push(item.key)
          this.islandQueue.push(item)
        })
        this.islandSeenKeys = this.islandSeenKeys.slice(-100)
        this.showNextIslandNotice()
      } catch (error) { /* 灵动岛属于附属体验，失败不能阻塞当前页面。 */ }
    },
    showNextIslandNotice() {
      if (this.islandCurrent || !this.islandQueue.length) return
      this.islandCurrent = this.islandQueue.shift()
    },
    dismissIslandNotice() {
      if (!this.islandCurrent) return
      this.islandCurrent = null
      window.clearTimeout(this.islandAdvanceTimer)
      this.islandAdvanceTimer = window.setTimeout(() => this.showNextIslandNotice(), 240)
    },
    selectIslandNotice(item) {
      this.dismissIslandNotice()
      if (!item) return
      if (item.type === "live") {
        markNotificationRead({ notification_id: Number(item.id) }).then(() => this.loadNotificationUnread()).catch(() => {})
        this.openDirectVideo(Number(item.target_id)).catch(() => {})
        return
      }
      if (item.type === "chat" && Number(item.sender_user_id) > 0) this.openDirectChat({ target_type: "user", target_id: Number(item.sender_user_id) })
    },
    resetDynamicIsland(userId = 0) {
      window.clearTimeout(this.islandAdvanceTimer)
      this.islandAdvanceTimer = 0; this.islandCurrent = null; this.islandQueue = []; this.islandSeenKeys = []
      this.islandLiveCursor = 0; this.islandChatCursor = 0; this.islandUserId = Number(userId || 0)
    },
    async pollNotificationState() {
      if (this.notificationPollingBusy || document.hidden || !this.currentUser) return
      this.notificationPollingBusy = true
      try { await Promise.all([this.loadNotificationUnread(), this.loadDynamicIslandNotices()]) } finally { this.notificationPollingBusy = false }
    },
    startNotificationPolling() {
      this.stopNotificationPolling()
      this.notificationPollTimer = window.setInterval(() => this.pollNotificationState(), 4000)
    },
    stopNotificationPolling() {
      if (this.notificationPollTimer) window.clearInterval(this.notificationPollTimer)
      this.notificationPollTimer = 0
    },
    handleAuthRequired() { this.currentUser = null; this.resetDynamicIsland(); this.studioVisible = false; this.profileVisible = false; this.messageVisible = false; this.discoverVisible = false; this.notificationUnread = 0; this.activeRootTab = "home"; this.$notice.warning(this.$t("errors.AUTH.REQUIRED")); this.showAuth("login", this.pendingAction) },
    handleAccountRestricted(event) {
      this.currentUser = null; this.studioVisible = false; this.profileVisible = false; this.messageVisible = false; this.discoverVisible = false; this.notificationUnread = 0; this.activeRootTab = "home"; const data = event.detail.data || {}
      this.$notice.confirm(data.reason_code ? this.$t(`errors.${data.reason_code}`) : this.$t("errors.AUTH.FORBIDDEN"), this.$t("errors.AUTH.FORBIDDEN"), { confirmButtonText: this.$t("common.retry"), cancelButtonText: this.$t("common.cancel"), type: "warning" }).then(() => { window.location.href = data.appeal_url || "/support/appeal" }).catch(() => {})
    },
    startCountdown(seconds) { this.stopCountdown(); this.countdown = seconds; this.countdownTimer = window.setInterval(() => { this.countdown -= 1; if (this.countdown <= 0) this.stopCountdown() }, 1000) },
    stopCountdown() { if (this.countdownTimer) window.clearInterval(this.countdownTimer); this.countdownTimer = null; this.countdown = 0 },
    resetAuthForm() { this.authForm.code = ""; this.authForm.password = ""; this.authForm.passwordConfirmation = ""; if (this.$refs.authForm) this.$refs.authForm.clearValidate() },
    formatCount(value) { return this.$compact(value) },
    async handleLocaleChanged() {
      updateSeo(this.activeRootTab)
      try {
        await this.loadChannels()
        await this.loadFeed()
        if (this.$refs.discoverPage) await this.$refs.discoverPage.reloadLocale()
      } catch (error) {
        // 统一请求层已经提示错误，语言切换事件不再向全局抛出未处理 Promise。
      }
    },
    formatDuration(seconds) { const value = Number(seconds || 0); return `${String(Math.floor(value / 60)).padStart(2, "0")}:${String(value % 60).padStart(2, "0")}` }
  },
  watch: {
    currentUser(value) {
      if (!value) { this.resetDynamicIsland(); return }
      const userId = Number(value.id || 0)
      if (userId !== this.islandUserId) this.resetDynamicIsland(userId)
    },
    activeRootTab(value) {
      updateSeo(value)
      const page = value === "message" ? "friends" : value
      behaviorAnalytics.enter(page, value === "home" ? this.activeChannel : "main")
    },
    activeChannel(value) {
      if (this.activeRootTab === "home") behaviorAnalytics.enter("home", value)
    }
  },
  created() {
    window.addEventListener("auth-required", this.handleAuthRequired)
    window.addEventListener("account-restricted", this.handleAccountRestricted)
    window.addEventListener("locale-changed", this.handleLocaleChanged)
    window.addEventListener("starlive:network-restored", this.handleNetworkRestored)
    window.addEventListener("pageshow", this.handlePendingPaymentResume)
    window.addEventListener("focus", this.handlePendingPaymentResume)
    document.addEventListener("visibilitychange", this.handlePendingPaymentResume)
  },
  mounted() {
    if (this.gameRechargeStandaloneVisible) {
      this.appReady = true
      return
    }
    if (this.paymentReturnVisible) {
      this.appReady = true
      return
    }
    behaviorAnalytics.start("home", this.activeChannel)
    this.initialize()
    this.startNotificationPolling()
    this.handlePendingPaymentResume()
  },
  beforeDestroy() {
    window.removeEventListener("auth-required", this.handleAuthRequired); window.removeEventListener("account-restricted", this.handleAccountRestricted); window.removeEventListener("locale-changed", this.handleLocaleChanged); window.removeEventListener("starlive:network-restored", this.handleNetworkRestored); window.removeEventListener("pageshow", this.handlePendingPaymentResume); window.removeEventListener("focus", this.handlePendingPaymentResume); document.removeEventListener("visibilitychange", this.handlePendingPaymentResume)
    window.clearTimeout(this.scrollTimer); window.clearTimeout(this.feedTapTimer); window.clearTimeout(this.feedStartupRetryTimer); window.clearTimeout(this.liveRoomSwitchTimer); window.clearTimeout(this.islandAdvanceTimer); this.stopNotificationPolling(); this.stopCountdown(); this.pauseAll(); behaviorAnalytics.destroy()
  }
}
</script>
